import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Package, Truck, User, MapPin, Phone, Mail, Calendar, Calendar as CalendarIcon, Clock, 
  CreditCard, Tag, FileText, RefreshCw, DollarSign, CheckCircle, 
  XCircle, AlertTriangle, RotateCcw, History, ExternalLink, Copy,
  ChevronRight, ShoppingBag, Globe, Shield, Percent, Building2, Edit2, Save, Loader2, Printer,
  MoreHorizontal, ChevronDown, Share2, Link, Navigation, Send, GitBranch, Plus, Minus, ArrowRight, Trash2, Download, Store, Hammer, Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';
import { flagForRecord } from '@/lib/countryFlags';
import { ShippingRatesCompare } from '@/components/ShippingRatesCompare';
import ImageLightbox from '@/components/ImageLightbox';
import DriverActionPanel from '@/components/DriverActionPanel';
import PaymentLinkButton from '@/components/PaymentLinkButton';
import FlipkartFulfillmentActions from '@/components/FlipkartFulfillmentActions';
import AmazonFulfillmentActions from '@/components/AmazonFulfillmentActions';
import { DanubeActions } from '@/components/DanubeActions';
import EditOrderDialog from '@/components/EditOrderDialog';
import { CarrefourActions } from '@/components/CarrefourActions';
import { detectChannel, getChannelMeta } from '@/lib/channels';
import SalesAttributionPanel from '@/components/SalesAttributionPanel';
import { DateInput } from '@/components/ui/date-input';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// CustomerTab component with editable Company Name and TRN
const CustomerTab = ({ order, copyToClipboard, onRefresh, isAdmin }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [companyName, setCompanyName] = useState(order.company_name || '');
  const [trnNumber, setTrnNumber] = useState(order.trn_number || '');

  useEffect(() => {
    setCompanyName(order.company_name || '');
    setTrnNumber(order.trn_number || '');
  }, [order]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_BASE_URL}/enterprise/orders/${order.id}/company-trn`,
        { company_name: companyName, trn_number: trnNumber },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Company & TRN updated successfully');
      setEditing(false);
      if (onRefresh) {
        onRefresh({ ...order, company_name: companyName, trn_number: trnNumber });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Customer Info */}
      <div className="bg-wt-paper rounded-lg border border-wt-border p-4">
        <h3 className="text-sm font-medium text-wt-text-muted mb-3 flex items-center gap-2">
          <User className="w-4 h-4" />
          Customer
        </h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            {order.customer_id || order.customer_email || order.customer_phone ? (
              <a
                href={
                  order.customer_id
                    ? `/customers?customer_id=${encodeURIComponent(order.customer_id)}`
                    : order.customer_email
                    ? `/customers?email=${encodeURIComponent(order.customer_email)}`
                    : `/customers?phone=${encodeURIComponent(order.customer_phone)}`
                }
                className="font-medium text-wt-text-primary hover:text-wt-gold hover:underline cursor-pointer"
                data-testid="customer-name-link"
                title="View customer profile"
              >
                {order.customer_name}
              </a>
            ) : (
              <span className="font-medium text-wt-text-primary">{order.customer_name}</span>
            )}
            {order.customer_orders_count > 1 && (
              <Badge variant="outline" className="border-wt-gold text-wt-gold">
                {order.customer_orders_count} orders
              </Badge>
            )}
          </div>
          {order.customer_email && (
            <div className="flex items-center gap-2 text-sm">
              <Mail className="w-4 h-4 text-wt-text-muted" />
              <a href={`mailto:${order.customer_email}`} className="text-blue-400 hover:underline">
                {order.customer_email}
              </a>
              <button onClick={() => copyToClipboard(order.customer_email)} className="text-wt-text-muted hover:text-wt-text-primary">
                <Copy className="w-3 h-3" />
              </button>
            </div>
          )}
          {order.customer_phone && (
            <div className="flex items-center gap-2 text-sm">
              <Phone className="w-4 h-4 text-wt-text-muted" />
              {(() => {
                const flag = flagForRecord(order, 'customer_phone_country_code', 'customer_phone');
                return flag ? (
                  <span
                    className="text-base leading-none"
                    title={order.customer_phone_country_code ? `+${order.customer_phone_country_code}` : ''}
                    data-testid="order-detail-phone-flag"
                  >
                    {flag}
                  </span>
                ) : null;
              })()}
              <a href={`tel:${order.customer_phone}`} className="text-blue-400 hover:underline" data-testid="order-detail-phone">
                {order.customer_phone}
              </a>
              <button onClick={() => copyToClipboard(order.customer_phone)} className="text-wt-text-muted hover:text-wt-text-primary">
                <Copy className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Company & Tax Number Info - Editable by Admin */}
      <div className="bg-wt-paper rounded-lg border border-wt-border p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-wt-text-muted flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Business Details (for Tax Invoice)
          </h3>
          {isAdmin && !editing && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setEditing(true)}
              className="text-wt-text-muted hover:text-wt-gold"
            >
              <Edit2 className="w-4 h-4" />
            </Button>
          )}
        </div>

        {editing ? (
          <div className="space-y-3">
            <div>
              <Label className="text-wt-text-muted text-xs">Company Name</Label>
              <Input
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Enter company name..."
                className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
              />
            </div>
            <div>
              <Label className="text-wt-text-muted text-xs">Tax Number (e.g. TRN, GSTIN, EIN, VAT No.)</Label>
              <Input
                value={trnNumber}
                onChange={(e) => setTrnNumber(e.target.value)}
                placeholder="Enter tax number for tax invoice..."
                className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
              />
            </div>
            <div className="flex gap-2 mt-3">
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saving}
                className="bg-wt-gold hover:bg-wt-gold-hover text-black"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                Save
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setEditing(false);
                  setCompanyName(order.company_name || '');
                  setTrnNumber(order.trn_number || '');
                }}
                className="text-wt-text-muted"
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-wt-text-muted">Company:</span>
              <span className="text-wt-text-primary">{order.company_name || 'Not specified'}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-wt-text-muted">Tax Number:</span>
              {order.trn_number ? (
                <>
                  <span className="text-wt-text-primary font-mono">{order.trn_number}</span>
                  <Badge className="bg-green-500/20 text-green-400 text-xs">Tax Registered</Badge>
                </>
              ) : (
                <span className="text-wt-text-secondary">Not provided</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Shipping Address */}
      <div className="bg-wt-paper rounded-lg border border-wt-border p-4">
        <h3 className="text-sm font-medium text-wt-text-muted mb-3 flex items-center gap-2">
          <MapPin className="w-4 h-4" />
          Shipping Address
        </h3>
        {order.shipping_address_full ? (
          <div className="text-sm text-wt-text-secondary space-y-1">
            <p>{order.shipping_address_full.name}</p>
            {order.shipping_address_full.company && (
              <p className="text-wt-gold">{order.shipping_address_full.company}</p>
            )}
            <p>{order.shipping_address_full.address1}</p>
            {order.shipping_address_full.address2 && <p>{order.shipping_address_full.address2}</p>}
            <p>
              {order.shipping_address_full.city}, {order.shipping_address_full.province} {order.shipping_address_full.zip}
            </p>
            <p>{order.shipping_address_full.country}</p>
            {order.shipping_address_full.phone && (
              <p className="text-wt-text-muted">{order.shipping_address_full.phone}</p>
            )}
          </div>
        ) : order.shipping_address && typeof order.shipping_address === 'object' ? (
          <div className="text-sm text-wt-text-secondary space-y-1">
            {order.shipping_address.first_name && (
              <p>{order.shipping_address.first_name} {order.shipping_address.last_name}</p>
            )}
            {order.shipping_address.address1 && <p>{order.shipping_address.address1}</p>}
            {order.shipping_address.address2 && <p>{order.shipping_address.address2}</p>}
            {(order.shipping_address.city || order.shipping_address.province) && (
              <p>{order.shipping_address.city}{order.shipping_address.province ? `, ${order.shipping_address.province}` : ''} {order.shipping_address.zip}</p>
            )}
            {order.shipping_address.country && <p>{order.shipping_address.country}</p>}
            {order.shipping_address.phone && <p className="text-wt-text-muted">{order.shipping_address.phone}</p>}
          </div>
        ) : (
          <p className="text-sm text-wt-text-secondary">
            {typeof order.shipping_address === 'string' ? order.shipping_address : 'N/A'}
          </p>
        )}
        
        {/* Customer Location Pin */}
        {order.delivery_location && order.delivery_location.lat && order.delivery_location.lng && (
          <div className="mt-3 pt-3 border-t border-wt-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                  <MapPin className="w-3 h-3 mr-1" />
                  Location Received
                </Badge>
                {order.delivery_location.source === 'customer_pin' && (
                  <span className="text-xs text-green-400">from tracking page</span>
                )}
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  const googleMapsUrl = `https://www.google.com/maps?q=${order.delivery_location.lat},${order.delivery_location.lng}`;
                  copyToClipboard(googleMapsUrl);
                  toast.success('Location URL copied to clipboard!');
                }}
                className="border-wt-border text-wt-text-primary hover:bg-wt-gold/20"
                data-testid="copy-location-url-btn"
              >
                <Copy className="w-3 h-3 mr-1" />
                Copy URL
              </Button>
            </div>
            <a
              href={`https://www.google.com/maps?q=${order.delivery_location.lat},${order.delivery_location.lng}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 flex items-center gap-2 text-sm text-blue-400 hover:underline"
            >
              <ExternalLink className="w-3 h-3" />
              Open in Google Maps
            </a>
          </div>
        )}
      </div>

      {/* Billing Address */}
      {order.billing_address_full && (
        <div className="bg-wt-paper rounded-lg border border-wt-border p-4">
          <h3 className="text-sm font-medium text-wt-text-muted mb-3 flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            Billing Address
          </h3>
          <div className="text-sm text-wt-text-secondary space-y-1">
            <p>{order.billing_address_full.name}</p>
            {order.billing_address_full.company && (
              <p className="text-wt-gold">{order.billing_address_full.company}</p>
            )}
            <p>{order.billing_address_full.address1}</p>
            {order.billing_address_full.address2 && <p>{order.billing_address_full.address2}</p>}
            <p>
              {order.billing_address_full.city}, {order.billing_address_full.province} {order.billing_address_full.zip}
            </p>
            <p>{order.billing_address_full.country}</p>
          </div>
        </div>
      )}
    </div>
  );
};

const statusConfig = {
  pending: { label: 'Pending', color: 'bg-yellow-500', textColor: 'text-yellow-400' },
  unfulfilled: { label: 'Pending', color: 'bg-yellow-500', textColor: 'text-yellow-400' },
  ready_for_dispatch: { label: 'Ready for Dispatch', color: 'bg-cyan-500', textColor: 'text-cyan-400' },
  accepted: { label: 'Accepted', color: 'bg-blue-500', textColor: 'text-blue-400' },
  booked: { label: 'Booked', color: 'bg-cyan-500', textColor: 'text-cyan-400' },
  picked: { label: 'Picked', color: 'bg-purple-500', textColor: 'text-purple-400' },
  picked_up: { label: 'In Transit', color: 'bg-purple-500', textColor: 'text-purple-400' },
  on_the_way: { label: 'On The Way', color: 'bg-orange-500', textColor: 'text-orange-400' },
  in_transit: { label: 'In Transit', color: 'bg-purple-500', textColor: 'text-purple-400' },
  courier_picked_up: { label: 'In Transit', color: 'bg-purple-500', textColor: 'text-purple-400' },
  courier_in_transit: { label: 'In Transit', color: 'bg-purple-500', textColor: 'text-purple-400' },
  out_for_delivery: { label: 'Out for Delivery', color: 'bg-orange-500', textColor: 'text-orange-400' },
  courier_out_for_delivery: { label: 'Out for Delivery', color: 'bg-orange-500', textColor: 'text-orange-400' },
  delivered: { label: 'Delivered', color: 'bg-green-500', textColor: 'text-green-400' },
  fulfilled: { label: 'Fulfilled', color: 'bg-green-500', textColor: 'text-green-400' },
  failed: { label: 'Failed', color: 'bg-red-500', textColor: 'text-red-400' },
  rto_initiated: { label: 'RTO Initiated', color: 'bg-red-500', textColor: 'text-red-400' },
  rto_in_transit: { label: 'RTO In Transit', color: 'bg-red-500', textColor: 'text-red-400' },
  rto_delivered: { label: 'RTO Delivered', color: 'bg-red-500', textColor: 'text-red-400' },
  reopened: { label: 'Reopened', color: 'bg-amber-500', textColor: 'text-amber-400' },
  cancelled: { label: 'Cancelled', color: 'bg-gray-500', textColor: 'text-gray-400' },
};

// Payment-status badge labels.
//   • `pending` → shown as "COD" because in this app every unpaid order
//     is a Cash-on-Delivery order. The underlying value stays `pending`
//     in the DB / API — only the *label* changes. When a COD order is
//     marked paid in Shopify the `paid` row is hit and the badge flips
//     to "Paid" automatically (no extra logic anywhere).
//   • `paid` / `partially_paid` / `refunded` / `partially_refunded` are
//     unchanged.
const paymentStatusConfig = {
  pending: { label: 'COD', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  pending_verification: { label: 'Pending Verification', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  paid: { label: 'Paid', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  partially_paid: { label: 'Partially Paid', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  partial: { label: 'Partially Paid', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  partial_refund: { label: 'Partially Refunded', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
  refunded: { label: 'Refunded', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  partially_refunded: { label: 'Partially Refunded', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
};

const OrderDetailPanel = ({ 
  open, 
  onClose, 
  order, 
  formatCurrency,
  onRefresh,
  onAssignDriver,
  onAssignDriverToShipment,  // New: For shipment-level driver assignment
  onBookCourier,
  onBookCourierForShipment,  // New: For shipment-level courier booking
  onUnassignCourier,
  onUnassignDriver,  // New: For unassigning driver from order
  onReopen,
  onReturnReplacement,
  onRefund,
  onViewAuditLog,
  onCreatePO,
  onCreateInvoice,
  onOverrideStatus,
  onViewPOD,
  onSplitShipment,
  isAdmin = false,
  canAssignDriver = false,
  canBookCourier = false,
  canManageReturns = false,
  canManageRefunds = false,
  canSyncShopify = false,
  canOverrideStatus = false,
  canCreatePaymentLink = false,
  canRequestIncentive = false,
  canMarkReadyForDispatch = false,
  canCreateInvoice = false,
  canCreatePO = false,
  canEditOrder = false,
  canCancelOrder = false
}) => {
  const navigate = useNavigate();
  const [syncing, setSyncing] = useState(false);
  const [createReturnOpen, setCreateReturnOpen] = useState(false);
  const [windowExpiredMsg, setWindowExpiredMsg] = useState('');
  const [returnItems, setReturnItems] = useState([]);
  const [returnType, setReturnType] = useState('return_refund');
  const [returnNotes, setReturnNotes] = useState('');
  const [creatingReturn, setCreatingReturn] = useState(false);
  const [syncingToShopify, setSyncingToShopify] = useState(false);
  const [ackingEditAlert, setAckingEditAlert] = useState(false);
  const [reschedDateInput, setReschedDateInput] = useState('');
  const [pinInput, setPinInput] = useState('');
  const [savingPin, setSavingPin] = useState(false);
  const [showPinInput, setShowPinInput] = useState(false);
  const [driverNoteInput, setDriverNoteInput] = useState(order?.driver_note || '');
  const [savingDriverNote, setSavingDriverNote] = useState(false);
  const [pushingShopify, setPushingShopify] = useState(false);
  const [editOrderOpen, setEditOrderOpen] = useState(false);
  const [cancelOrderOpen, setCancelOrderOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancellingOrder, setCancellingOrder] = useState(false);
  const [deliveryDateOpen, setDeliveryDateOpen] = useState(false);
  const [deliveryDateInput, setDeliveryDateInput] = useState('');
  const [savingDeliveryDate, setSavingDeliveryDate] = useState(false);
  const [auditActorMap, setAuditActorMap] = useState({});

  // Fetch audit log when the panel opens → map status → who set it
  useEffect(() => {
    if (!order?.id) return;
    let alive = true;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_BASE_URL}/orders/${order.id}/audit-log`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const map = {};
        // logs are sorted newest-first; keep the most recent actor per status
        (res.data.audit_logs || []).forEach((l) => {
          if (l.new_status && l.user_name && !map[l.new_status]) map[l.new_status] = l.user_name;
        });
        if (alive) setAuditActorMap(map);
      } catch (e) { /* timeline still renders without attribution */ }
    })();
    return () => { alive = false; };
  }, [order?.id]);

  const actorFor = (status, fieldName) => order?.[fieldName] || auditActorMap[status] || null;
  const byLine = (status, fieldName) => {
    const a = actorFor(status, fieldName);
    return a ? `by ${a}` : undefined;
  };

  const saveDeliveryDate = async (dateVal) => {
    setSavingDeliveryDate(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/orders/${order.id}/reschedule-date`,
        { date: dateVal },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(dateVal ? 'Delivery date set' : 'Delivery date cleared');
      setDeliveryDateOpen(false);
      const res = await axios.get(`${API_BASE_URL}/orders/${order.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onRefresh && onRefresh(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save date');
    } finally {
      setSavingDeliveryDate(false);
    }
  };

  const handleCancelOrder = async () => {
    setCancellingOrder(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_BASE_URL}/orders/${order.id}/cancel`,
        { reason: cancelReason.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Order cancelled');
      setCancelOrderOpen(false);
      setCancelReason('');
      if (onRefresh && res.data.order) onRefresh(res.data.order);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel order');
    } finally {
      setCancellingOrder(false);
    }
  };

  useEffect(() => { setDriverNoteInput(order?.driver_note || ''); }, [order?.id, order?.driver_note]);

  const saveDriverNote = async () => {
    setSavingDriverNote(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/orders/${order.id}/driver-note`,
        { note: driverNoteInput },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Driver note saved');
      if (onRefresh) {
        const refreshRes = await axios.get(
          `${API_BASE_URL}/orders/${order.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        onRefresh(refreshRes.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save driver note');
    } finally {
      setSavingDriverNote(false);
    }
  };

  const saveLocationPin = async () => {
    const m = pinInput.trim().match(/^(-?\d+(\.\d+)?)[\s,]+(-?\d+(\.\d+)?)$/);
    if (!m) {
      toast.error('Paste coordinates like: 25.110054, 55.224058');
      return;
    }
    setSavingPin(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/orders/${order.id}/location-pin`,
        { coordinates: pinInput.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Location pin saved');
      setPinInput('');
      setShowPinInput(false);
      if (onRefresh) {
        const refreshRes = await axios.get(
          `${API_BASE_URL}/orders/${order.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        onRefresh(refreshRes.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save location pin');
    } finally {
      setSavingPin(false);
    }
  };
  const [savingReschedDate, setSavingReschedDate] = useState(false);
  const [showReassignDriver, setShowReassignDriver] = useState(false);
  const [reassignDrivers, setReassignDrivers] = useState([]);
  const [reassignDriverId, setReassignDriverId] = useState('');
  const [reassigningDriver, setReassigningDriver] = useState(false);

  const openReassignDriver = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/drivers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReassignDrivers(res.data.drivers || []);
      setShowReassignDriver(true);
    } catch (e) {
      toast.error(e.response?.status === 403 ? 'You do not have permission to reassign drivers' : 'Failed to load drivers');
    }
  };

  const handleReassignDriver = async () => {
    if (!reassignDriverId) return;
    setReassigningDriver(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/orders/${order.id}/reassign`,
        { driver_id: reassignDriverId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Driver reassigned');
      setShowReassignDriver(false);
      setReassignDriverId('');
      const res = await axios.get(`${API_BASE_URL}/orders/${order.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onRefresh && onRefresh(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reassign driver');
    } finally {
      setReassigningDriver(false);
    }
  };

  const handleSaveRescheduleDate = async () => {
    if (!reschedDateInput) { toast.error('Pick a date first'); return; }
    setSavingReschedDate(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/orders/${order.id}/reschedule-date`,
        { date: reschedDateInput },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('New delivery date saved');
      const res = await axios.get(`${API_BASE_URL}/orders/${order.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onRefresh && onRefresh(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save date');
    } finally {
      setSavingReschedDate(false);
    }
  };

  const handleAcknowledgeEditAlert = async () => {
    setAckingEditAlert(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/orders/${order.id}/edit-alert/acknowledge`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Marked as rechecked');
      const res = await axios.get(`${API_BASE_URL}/orders/${order.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onRefresh && onRefresh(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to acknowledge');
    } finally {
      setAckingEditAlert(false);
    }
  };
  const [activeTab, setActiveTab] = useState('details');
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [incentiveStatus, setIncentiveStatus] = useState(null);
  const [requestingIncentive, setRequestingIncentive] = useState(false);
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  const [splitShipments, setSplitShipments] = useState([]);
  const [splitting, setSplitting] = useState(false);
  const [canSplit, setCanSplit] = useState(false);
  const [splitError, setSplitError] = useState(null);
  const [incentiveSettings, setIncentiveSettings] = useState(null);
  const [selectedShipmentNumber, setSelectedShipmentNumber] = useState(null);  // For shipment-level assignment
  
  // Packing slip state
  const [packingSlipOpen, setPackingSlipOpen] = useState(false);
  const [packingSlipData, setPackingSlipData] = useState(null);
  const [loadingPackingSlip, setLoadingPackingSlip] = useState(false);
  const [markingReadyForDispatch, setMarkingReadyForDispatch] = useState(false);
  const [syncingTracking, setSyncingTracking] = useState(false);
  // `refreshingFromShopify` removed alongside the deleted Refresh
  // address button — the single Sync from Shopify button now does
  // the full pull including addresses.

  // Manual store credit (recovery action) — opens a small dialog where
  // admin issues credit against this order when the journey's
  // Add Store Credit node didn't post (skipped / failed). Pushes to
  // Opswarex ledger + Shopify store credit account.
  const [issueCreditOpen, setIssueCreditOpen] = useState(false);
  const [creditForm, setCreditForm] = useState({
    amount_type: 'fixed',
    amount: '',
    percentage: '',
    reason: '',
    expires_in_days: 30,
    push_to_shopify: true,
  });
  const [issuingCredit, setIssuingCredit] = useState(false);
  const [creditResult, setCreditResult] = useState(null);

  const openIssueCreditDialog = () => {
    setCreditResult(null);
    setCreditForm({
      amount_type: 'fixed',
      amount: '',
      percentage: '5',
      reason: `Credit for order ${order?.order_number || ''}`.trim(),
      expires_in_days: 30,
      push_to_shopify: !!order?.shopify_order_id,
    });
    setIssueCreditOpen(true);
  };

  const handleIssueCredit = async () => {
    if (!order?.id) return;
    if (creditForm.amount_type === 'fixed' && !(parseFloat(creditForm.amount) > 0)) {
      toast.error('Enter an amount greater than 0');
      return;
    }
    if (creditForm.amount_type === 'percentage' && !(parseFloat(creditForm.percentage) > 0)) {
      toast.error('Enter a percentage greater than 0');
      return;
    }
    setIssuingCredit(true);
    try {
      const token = localStorage.getItem('token');
      const body = {
        amount_type: creditForm.amount_type,
        reason: creditForm.reason,
        expires_in_days: Number(creditForm.expires_in_days) || 0,
        push_to_shopify: !!creditForm.push_to_shopify,
      };
      if (creditForm.amount_type === 'fixed') {
        body.amount = parseFloat(creditForm.amount);
      } else {
        body.percentage = parseFloat(creditForm.percentage);
      }
      const resp = await axios.post(
        `${API_BASE_URL}/orders/${order.id}/issue-store-credit`,
        body,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setCreditResult(resp.data);
      const sp = resp.data?.shopify_push || {};
      if (sp.success) {
        toast.success(`Issued ${resp.data.amount} — pushed to Shopify`);
      } else if (sp.attempted) {
        toast.warning(`Local credit posted, Shopify push FAILED: ${sp.error || 'unknown'}`);
      } else {
        toast.success(`Local credit of ${resp.data.amount} posted (Shopify push not attempted)`);
      }
      onRefresh && onRefresh(order);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to issue credit');
    } finally {
      setIssuingCredit(false);
    }
  };

  // Handle sync tracking status
  const handleSyncTrackingStatus = async () => {
    if (!order?.awb_number && !order?.courier_awb) {
      toast.error('No AWB number found');
      return;
    }
    
    setSyncingTracking(true);
    try {
      const token = localStorage.getItem('token');
      const awb = order.awb_number || order.courier_awb;
      const courierType = order.courier_type || 'delhivery';
      
      const response = await axios.post(
        `${API_BASE_URL}/v1/couriers/tracking/sync-single`,
        { awb_number: awb, courier_type: courierType, order_id: order.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(`Status updated to: ${response.data.status?.replace(/_/g, ' ')}`);
        // Refresh order data
        if (onRefresh) {
          const refreshRes = await axios.get(
            `${API_BASE_URL}/orders/${order.id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          onRefresh(refreshRes.data);
        }
      } else {
        toast.error(response.data.error || 'Failed to sync tracking');
      }
    } catch (err) {
      console.error('Sync tracking error:', err);
      toast.error(err.response?.data?.detail || 'Failed to sync tracking status');
    } finally {
      setSyncingTracking(false);
    }
  };

  // Fetch incentive status when order changes
  useEffect(() => {
    if (order?.id && canRequestIncentive) {
      fetchIncentiveStatus();
      fetchIncentiveSettings();
    }
  }, [order?.id, canRequestIncentive]);

  const fetchIncentiveStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/orders/${order.id}/incentive-status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIncentiveStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch incentive status:', error);
    }
  };

  const fetchIncentiveSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/settings/order-incentive`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIncentiveSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch incentive settings:', error);
    }
  };

  const handleRequestIncentive = async () => {
    setRequestingIncentive(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/orders/${order.id}/request-incentive`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.auto_approved) {
        toast.success(`Incentive of ${formatCurrency(response.data.request.amount, order?.currency)} added to your balance!`);
      } else {
        toast.success(`Incentive request submitted for ${formatCurrency(response.data.request.amount, order?.currency)}`);
      }
      
      fetchIncentiveStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request incentive');
    } finally {
      setRequestingIncentive(false);
    }
  };

  // Ref for scrollable content area
  const contentRef = useRef(null);
  
  // Scroll to top when order changes or panel opens
  useEffect(() => {
    if (open && contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [open, order?.id]);

  // Split shipment check - must be before early return
  const checkCanSplit = useCallback(async () => {
    if (!order?.id) {
      setCanSplit(false);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/orders/${order.id}/can-split`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCanSplit(response.data.can_split);
      setSplitError(response.data.can_split ? null : response.data.reason);
    } catch (error) {
      setCanSplit(false);
      setSplitError(error.response?.data?.detail || 'Cannot check split eligibility');
    }
  }, [order?.id]);

  useEffect(() => {
    checkCanSplit();
  }, [checkCanSplit]);

  if (!order) return null;

  const handleCreateZohoInvoice = async () => {
    setCreatingInvoice(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/enterprise/orders/${order.id}/create-zoho-invoice`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Invoice ${response.data.zoho_invoice_number} created in Zoho Books`);
      if (onRefresh) onRefresh({ ...order, zoho_invoice_id: response.data.zoho_invoice_id, zoho_invoice_number: response.data.zoho_invoice_number });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setCreatingInvoice(false);
    }
  };

  const handleSyncFromShopify = async () => {
    // Allow sync for orders that have shopify_order_id (whether from Shopify or synced from lead)
    if (!order.shopify_order_id) {
      toast.error('This order is not linked to Shopify');
      return;
    }

    setSyncing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/orders/${order.id}/sync-shopify`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Order synced from Shopify successfully');
      if (onRefresh) onRefresh(response.data.order);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync order');
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncFulfillmentToShopify = async () => {
    // Allow sync for orders that have shopify_order_id
    if (!order.shopify_order_id) {
      toast.error('This order is not linked to Shopify');
      return;
    }

    setSyncingToShopify(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/orders/${order.id}/sync-fulfillment-to-shopify`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message || 'Sync failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync to Shopify');
    } finally {
      setSyncingToShopify(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  // Fetch and open packing slip (same PDF generator as bulk printing,
  // so single + bulk slips share one format — includes driver QR)
  const handlePrintPackingSlip = async () => {
    setLoadingPackingSlip(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/orders/bulk/packing-slips`,
        { order_ids: [order.id] },
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      if (onRefresh) {
        const res = await axios.get(`${API_BASE_URL}/orders/${order.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        onRefresh(res.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load packing slip');
    } finally {
      setLoadingPackingSlip(false);
    }
  };

  // `handleRefreshFromShopify` removed — `handleSyncFromShopify`
  // already pulls the full order (incl. addresses) from Shopify.


  // Mark order as Ready for Dispatch
  const handleMarkReadyForDispatch = async () => {
    setMarkingReadyForDispatch(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/orders/${order.id}/status`,
        { status: 'ready_for_dispatch' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Order marked as Ready for Dispatch');
      if (onRefresh) {
        const response = await axios.get(`${API_BASE_URL}/orders/${order.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        onRefresh(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update order status');
    } finally {
      setMarkingReadyForDispatch(false);
    }
  };

  const initializeSplitShipments = () => {
    // Support both 'line_items' (Shopify) and 'items' (custom orders)
    const orderItems = order?.line_items || order?.items || [];
    if (!orderItems.length) return;
    
    // Use INDEX-based line_item_id (idx-N) so that two variants of the same
    // product (which share product_id) don't collide and collapse into one.
    // Default: Put all items in shipment 1, create empty shipment 2.
    setSplitShipments([
      {
        id: 1,
        items: orderItems.map((item, idx) => ({
          line_item_id: `idx-${idx}`,
          original_id: item.id || item.product_id || null,
          title: item.title || item.name || 'Item',
          variant_title: item.variant_title || '',
          quantity: item.quantity || 1,
          max_quantity: item.quantity || 1,
          price: item.price || 0,
          sku: item.sku || '',
          image: item.image_url || item.image,
        })),
      },
      { id: 2, items: [] },
    ]);
    setSplitDialogOpen(true);
  };

  const updateShipmentItemQuantity = (shipmentId, lineItemId, newQuantity) => {
    setSplitShipments(prev => {
      const shipments = [...prev];
      const shipmentIdx = shipments.findIndex(s => s.id === shipmentId);
      if (shipmentIdx === -1) return prev;
      
      const itemIdx = shipments[shipmentIdx].items.findIndex(i => i.line_item_id === lineItemId);
      if (itemIdx === -1) return prev;
      
      shipments[shipmentIdx].items[itemIdx].quantity = Math.max(0, newQuantity);
      return shipments;
    });
  };

  const moveItemToShipment = (fromShipmentId, toShipmentId, lineItemId, quantity) => {
    setSplitShipments(prev => {
      const shipments = [...prev];
      const fromIdx = shipments.findIndex(s => s.id === fromShipmentId);
      const toIdx = shipments.findIndex(s => s.id === toShipmentId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      
      const fromItem = shipments[fromIdx].items.find(i => i.line_item_id === lineItemId);
      if (!fromItem || fromItem.quantity < quantity) return prev;
      
      // Reduce from source
      fromItem.quantity -= quantity;
      
      // Add to target
      const toItem = shipments[toIdx].items.find(i => i.line_item_id === lineItemId);
      if (toItem) {
        toItem.quantity += quantity;
      } else {
        shipments[toIdx].items.push({
          ...fromItem,
          quantity: quantity
        });
      }
      
      // Remove if quantity is 0
      if (fromItem.quantity === 0) {
        shipments[fromIdx].items = shipments[fromIdx].items.filter(i => i.line_item_id !== lineItemId);
      }
      
      return shipments;
    });
  };

  const addShipment = () => {
    setSplitShipments(prev => [
      ...prev,
      { id: prev.length + 1, items: [] }
    ]);
  };

  const removeShipment = (shipmentId) => {
    if (splitShipments.length <= 2) {
      toast.error('Must have at least 2 shipments');
      return;
    }
    
    setSplitShipments(prev => {
      const shipmentToRemove = prev.find(s => s.id === shipmentId);
      if (!shipmentToRemove) return prev;
      
      // Move items back to first shipment
      const remaining = prev.filter(s => s.id !== shipmentId);
      if (shipmentToRemove.items.length > 0 && remaining.length > 0) {
        shipmentToRemove.items.forEach(item => {
          const existingItem = remaining[0].items.find(i => i.line_item_id === item.line_item_id);
          if (existingItem) {
            existingItem.quantity += item.quantity;
          } else {
            remaining[0].items.push({ ...item });
          }
        });
      }
      
      return remaining;
    });
  };

  const calculateShipmentTotal = (shipment) => {
    return shipment.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const validateSplit = () => {
    // Check all shipments have items
    const emptyShipments = splitShipments.filter(s => s.items.length === 0 || s.items.every(i => i.quantity === 0));
    if (emptyShipments.length > 0) {
      toast.error('All shipments must have at least one item');
      return false;
    }
    
    // Check total quantities match original - support both 'line_items' and 'items'
    // Key by INDEX (idx-N) to match initializeSplitShipments / backend split_order.
    const orderItems = order.line_items || order.items || [];
    const originalItems = {};
    orderItems.forEach((item, idx) => {
      originalItems[`idx-${idx}`] = item.quantity || 1;
    });
    
    const allocatedItems = {};
    splitShipments.forEach(shipment => {
      shipment.items.forEach(item => {
        allocatedItems[item.line_item_id] = (allocatedItems[item.line_item_id] || 0) + item.quantity;
      });
    });
    
    for (const [itemId, originalQty] of Object.entries(originalItems)) {
      const allocatedQty = allocatedItems[itemId] || 0;
      if (allocatedQty !== originalQty) {
        toast.error(`Item quantities don't match original order`);
        return false;
      }
    }
    
    return true;
  };

  const handleSplitOrder = async () => {
    if (!validateSplit()) return;
    
    setSplitting(true);
    try {
      const token = localStorage.getItem('token');
      
      // Format shipments for API
      const shipmentsData = splitShipments
        .filter(s => s.items.length > 0 && s.items.some(i => i.quantity > 0))
        .map(s => ({
          items: s.items
            .filter(i => i.quantity > 0)
            .map(i => ({
              line_item_id: i.line_item_id,
              quantity: i.quantity
            }))
        }));
      
      const response = await axios.post(
        `${API_BASE_URL}/orders/${order.id}/split`,
        { shipments: shipmentsData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message || 'Order split successfully');
      setSplitDialogOpen(false);
      
      // Refresh order list in a separate try-catch to avoid hiding success
      if (onRefresh) {
        try {
          onRefresh();
        } catch (refreshError) {
          console.error('Error refreshing orders:', refreshError);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to split order');
    } finally {
      setSplitting(false);
    }
  };

  // Print the packing slip
  const printPackingSlip = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow || !packingSlipData) return;
    
    const isPending = packingSlipData.is_pending || (packingSlipData.payment_status || 'pending').toLowerCase() !== 'paid';
    const paymentStatusText = (packingSlipData.payment_status || 'pending').toUpperCase();
    const paymentColor = paymentStatusText === 'PAID' ? '#22c55e' : '#ef4444';
    const collectableAmount = packingSlipData.collectable_amount || packingSlipData.total || 0;
    
    // Get currency from order or packingSlipData
    const orderCurrency = packingSlipData.currency || order?.currency || 'INR';
    const formattedCollectAmount = formatCurrency(collectableAmount, orderCurrency);
    
    // Build address display - use full_address if available, otherwise build from parts
    let addressHtml = '';
    if (packingSlipData.shipping_address?.full_address) {
      // Full address is a single string
      addressHtml = packingSlipData.shipping_address.full_address.replace(/,\s*/g, '<br>');
    } else {
      // Build from individual fields
      const addr = packingSlipData.shipping_address || {};
      const parts = [
        addr.address1,
        addr.address2,
        [addr.city, addr.province, addr.zip].filter(Boolean).join(', '),
        addr.country
      ].filter(Boolean);
      addressHtml = parts.join('<br>');
    }
    
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Packing Slip - ${packingSlipData.order_name}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; font-size: 12px; padding: 20px; }
          .header { display: flex; justify-content: space-between; margin-bottom: 15px; border-bottom: 2px solid #000; padding-bottom: 15px; }
          .logo { font-size: 20px; font-weight: bold; }
          .order-info { text-align: right; }
          .order-number { font-size: 16px; font-weight: bold; }
          
          .payment-banner { background: ${paymentColor}; color: white; padding: 12px 20px; margin-bottom: 15px; display: flex; justify-content: space-between; align-items: center; }
          .payment-status { font-size: 18px; font-weight: bold; }
          .collect-amount { font-size: 18px; font-weight: bold; background: #000; padding: 5px 15px; border-radius: 5px; }
          .driver-info { background: #3b82f6; color: white; padding: 8px 15px; margin-bottom: 15px; font-size: 13px; }
          
          .section { margin-bottom: 15px; }
          .section-title { font-weight: bold; margin-bottom: 5px; background: #f0f0f0; padding: 5px; }
          .address { line-height: 1.6; padding: 10px; border: 1px solid #ddd; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; vertical-align: middle; }
          th { background: #f5f5f5; font-weight: bold; }
          .rack { background: #ffe4b5; font-weight: bold; font-family: monospace; text-align: center; }
          .qty { text-align: center; font-weight: bold; }
          .item-cell { display: flex; align-items: center; gap: 10px; }
          .item-img { width: 50px; height: 50px; object-fit: cover; border: 1px solid #ddd; border-radius: 4px; }
          .item-details { flex: 1; }
          .notes { margin-top: 15px; padding: 10px; border: 1px dashed #999; background: #fffbeb; }
          .footer { margin-top: 20px; padding-top: 15px; border-top: 1px solid #ddd; font-size: 10px; text-align: center; color: #666; }
          @media print { 
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } 
            .payment-banner { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">WOODEN TWIST</div>
          <div class="order-info">
            <div class="order-number">PACKING SLIP</div>
            <div>${packingSlipData.order_name}</div>
            <div>${new Date(packingSlipData.created_at).toLocaleDateString()}</div>
          </div>
        </div>
        
        <div class="payment-banner">
          <span class="payment-status">PAYMENT: ${paymentStatusText}</span>
          ${isPending ? `<span class="collect-amount">COLLECT: ${formattedCollectAmount}</span>` : ''}
        </div>
        
        ${packingSlipData.driver_name ? `
        <div class="driver-info">
          <strong>Assigned Driver:</strong> ${packingSlipData.driver_name}
        </div>
        ` : ''}
        
        <div class="section">
          <div class="section-title">SHIP TO</div>
          <div class="address">
            <strong>${packingSlipData.shipping_address?.name || packingSlipData.customer?.name || ''}</strong><br>
            ${addressHtml}<br>
            <strong>Phone:</strong> ${packingSlipData.shipping_address?.phone || packingSlipData.customer?.phone || 'N/A'}
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">ITEMS (${packingSlipData.item_count} items, ${packingSlipData.total_quantity} units)</div>
          <table>
            <thead>
              <tr>
                <th style="width: 70px;">RACK</th>
                <th>ITEM</th>
                <th style="width: 100px;">SKU</th>
                <th style="width: 50px;" class="qty">QTY</th>
              </tr>
            </thead>
            <tbody>
              ${packingSlipData.items.map(item => `
                <tr>
                  <td class="rack">${item.rack_number || '-'}</td>
                  <td>
                    <div class="item-cell">
                      ${item.image || item.image_url ? `<img src="${item.image || item.image_url}" class="item-img" onerror="this.style.display='none'" />` : ''}
                      <div class="item-details">
                        ${item.name || item.title || 'Unknown Item'}${item.variant_title ? '<br><small style="color:#666;">' + item.variant_title + '</small>' : ''}
                      </div>
                    </div>
                  </td>
                  <td style="font-family: monospace; font-size: 10px;">${item.sku || '-'}</td>
                  <td class="qty">${item.quantity || 1}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
        
        ${packingSlipData.notes ? `
        <div class="notes">
          <strong>Order Notes:</strong> ${packingSlipData.notes}
        </div>
        ` : ''}
        
        <div class="footer">
          Printed on ${new Date().toLocaleString()}
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      // Mark packing slip as printed after print dialog opens
      markPackingSlipPrinted();
    }, 250);
  };

  // Mark packing slip as printed and add tag
  const markPackingSlipPrinted = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/orders/${order.id}/packing-slip/printed`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Fetch the updated order to get new status
      try {
        const orderResponse = await axios.get(
          `${API_BASE_URL}/orders/${order.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Refresh with the updated order data
        if (onRefresh) {
          onRefresh(orderResponse.data);
        }
      } catch (fetchErr) {
        // If fetch fails, still try basic refresh
        console.error('Failed to fetch updated order:', fetchErr);
        if (onRefresh) {
          // Pass order with updated flags (only mark packed if the backend did)
          onRefresh({
            ...order,
            packing_slip_printed: true,
            ...(response.data.status_changed ? { fulfillment_status: 'packed', status: 'packed' } : {})
          });
        }
      }
      
      // Show appropriate toast message
      const statusMsg = response.data.status_changed ? ' → Packed' : '';
      if (response.data.shopify_synced) {
        toast.success(`Packing slip printed${statusMsg} & tag synced to Shopify`);
      } else if (response.data.shopify_error) {
        toast.success(`Packing slip printed${statusMsg} (Shopify sync failed)`);
      } else {
        toast.success(`Packing slip printed${statusMsg}`);
      }
    } catch (error) {
      // Don't show error for print tracking - it's not critical
      console.error('Failed to mark packing slip as printed:', error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  // Pick the most informative status the same way `OrdersPage.getOrderTab`
  // does — courier_status (when present) wins, else server `status`,
  // else the Shopify-mirror `fulfillment_status`. Without this, orders
  // that are in transit / out-for-delivery in Opswarex show "Pending"
  // here just because Shopify hasn't been notified yet (the very bug
  // the Shopify Sync tab was added to surface).
  const effectiveFulfillmentStatus = (
    order.courier_status ||
    order.status ||
    order.fulfillment_status ||
    'pending'
  );
  const status = statusConfig[effectiveFulfillmentStatus] || statusConfig.pending;
  const paymentStatus = (order.payment_status === 'pending_verification' && order.payment_verification?.status === 'rejected')
    ? { label: 'Proof Rejected', color: 'bg-red-500/20 text-red-400 border-red-500/30' }
    : (paymentStatusConfig[order.payment_status] || paymentStatusConfig.pending);
  const financialStatus = paymentStatusConfig[order.financial_status] || paymentStatus;
  const isFulfilled = ['delivered', 'fulfilled'].includes(order.fulfillment_status);
  const isFailed = order.fulfillment_status === 'failed';
  const isCancelled = order.fulfillment_status === 'cancelled';

  const openCreateReturnDialog = () => {
    setReturnItems((order.items || []).map(it => ({
      sku: it.sku || '', name: it.name || it.title || '',
      quantity: it.quantity || 1, selected: false,
      quantity_returning: it.quantity || 1, reason: ''
    })));
    setReturnType('return_refund');
    setReturnNotes('');
    setWindowExpiredMsg('');
    setCreateReturnOpen(true);
  };

  const handleCreateReturn = async (overrideWindow = false) => {
    const selected = returnItems.filter(it => it.selected && it.sku);
    if (!selected.length) {
      toast.error('Select at least one item to return');
      return;
    }
    if (selected.some(it => !it.reason)) {
      toast.error('Add a reason for each selected item');
      return;
    }
    setCreatingReturn(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/v1/returns`, {
        order_id: order.id,
        return_type: returnType,
        customer_notes: returnNotes || null,
        source: 'manual',
        override_window: overrideWindow,
        items: selected.map(it => ({
          sku: it.sku,
          product_name: it.name || it.sku,
          quantity_ordered: Number(it.quantity) || 1,
          quantity_returning: Math.min(Number(it.quantity_returning) || 1, Number(it.quantity) || 1),
          reason: it.reason
        }))
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(overrideWindow ? 'Return created as exception (window expired)' : 'Return created — manage it from the Returns page');
      setWindowExpiredMsg('');
      setCreateReturnOpen(false);
      try {
        const r2 = await axios.get(`${API_BASE_URL}/orders/${order.id}`, { headers: { Authorization: `Bearer ${token}` } });
        if (onRefresh) onRefresh(r2.data);
      } catch { /* refresh is best-effort */ }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to create return';
      if (typeof detail === 'string' && detail.toLowerCase().includes('return window')) {
        setWindowExpiredMsg(detail);
      } else {
        toast.error(detail);
      }
    } finally {
      setCreatingReturn(false);
    }
  };

  return (
    <>
      {/* Create Return Dialog */}
      <Dialog open={createReturnOpen} onOpenChange={setCreateReturnOpen}>
        <DialogContent className="max-w-lg bg-wt-paper border-wt-border text-wt-text-primary max-h-[85vh] overflow-y-auto" data-testid="create-return-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-400">
              <RotateCcw className="w-5 h-5" />
              Create Return — {order.order_number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-wt-text-muted">Return Type</Label>
              <select
                value={returnType}
                onChange={(e) => setReturnType(e.target.value)}
                className="w-full h-9 mt-1 rounded-md border border-wt-border bg-wt-bg text-wt-text-primary text-sm px-2"
                data-testid="return-type-select"
              >
                <option value="return_refund">Return & Refund</option>
                <option value="refund_only">Refund Only (no pickup)</option>
                <option value="replace_same">Replace (Same Item)</option>
                <option value="replace_different">Replace (Different Item)</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-wt-text-muted">Items to Return</Label>
              {returnItems.map((it, idx) => (
                <div key={idx} className={`rounded-lg border p-3 space-y-2 ${it.selected ? 'border-purple-500/40 bg-purple-500/5' : 'border-wt-border bg-wt-bg'}`}>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={it.selected}
                      onChange={(e) => setReturnItems(prev => prev.map((p, i) => i === idx ? { ...p, selected: e.target.checked } : p))}
                      data-testid={`return-item-check-${it.sku}`}
                    />
                    <span className="text-sm font-medium truncate">{it.name} <span className="text-wt-text-muted">({it.sku}) × {it.quantity}</span></span>
                  </label>
                  {it.selected && (
                    <div className="flex gap-2">
                      <select
                        value={it.reason}
                        onChange={(e) => setReturnItems(prev => prev.map((p, i) => i === idx ? { ...p, reason: e.target.value } : p))}
                        className="flex-1 h-9 rounded-md border border-wt-border bg-wt-bg text-wt-text-primary text-sm px-2"
                        data-testid={`return-item-reason-${it.sku}`}
                      >
                        <option value="">Select reason…</option>
                        <option value="Damaged">Damaged</option>
                        <option value="Defective">Defective</option>
                        <option value="Wrong item received">Wrong item received</option>
                        <option value="Not as described">Not as described</option>
                        <option value="Changed mind">Changed mind</option>
                        <option value="Size/fit issue">Size/fit issue</option>
                        <option value="Other">Other</option>
                      </select>
                      <Input
                        type="number"
                        min="1"
                        max={it.quantity}
                        value={it.quantity_returning}
                        onChange={(e) => setReturnItems(prev => prev.map((p, i) => i === idx ? { ...p, quantity_returning: e.target.value } : p))}
                        className="w-20 bg-wt-bg border-wt-border"
                        data-testid={`return-item-qty-${it.sku}`}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div>
              <Label className="text-wt-text-muted">Notes (optional)</Label>
              <Input
                value={returnNotes}
                onChange={(e) => setReturnNotes(e.target.value)}
                placeholder="e.g. customer called about damaged leg"
                className="mt-1 bg-wt-bg border-wt-border"
                data-testid="return-notes-input"
              />
            </div>
            {windowExpiredMsg && (
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 space-y-2" data-testid="window-expired-banner">
                <div className="flex items-center gap-2 text-amber-400 text-sm font-medium">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  {windowExpiredMsg}
                </div>
                <p className="text-xs text-wt-text-muted">
                  For escalations/goodwill you can still create this return as an exception. It will be marked as a window-override in the return timeline.
                </p>
                <Button
                  size="sm"
                  onClick={() => handleCreateReturn(true)}
                  disabled={creatingReturn}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="create-return-exception-btn"
                >
                  {creatingReturn && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                  Create as Exception
                </Button>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setCreateReturnOpen(false)}>Cancel</Button>
              <Button
                onClick={() => handleCreateReturn(false)}
                disabled={creatingReturn}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="submit-create-return-btn"
              >
                {creatingReturn && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                Create Return
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Packing Slip Preview Dialog */}
      <Dialog open={packingSlipOpen} onOpenChange={setPackingSlipOpen}>
        <DialogContent className="max-w-2xl bg-wt-paper border-wt-border text-wt-text-primary max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-wt-gold">
              <Printer className="w-5 h-5" />
              Packing Slip Preview - {packingSlipData?.order_name}
            </DialogTitle>
          </DialogHeader>
          
          {packingSlipData && (
            <div className="space-y-4">
              {/* Payment Status Banner */}
              {(() => {
                const isPending = packingSlipData.is_pending || (packingSlipData.payment_status || 'pending').toLowerCase() !== 'paid';
                const collectableAmount = packingSlipData.collectable_amount || packingSlipData.total || 0;
                return (
                  <div className={`rounded-lg p-4 ${
                    packingSlipData.payment_status === 'paid' ? 'bg-green-500/20 border border-green-500/50' :
                    'bg-red-500/20 border border-red-500/50'
                  }`}>
                    <div className="flex justify-between items-center">
                      <span className={`text-lg font-bold ${
                        packingSlipData.payment_status === 'paid' ? 'text-green-400' : 'text-red-400'
                      }`}>
                        PAYMENT: {(packingSlipData.payment_status || 'pending').toUpperCase()}
                      </span>
                      {isPending && (
                        <span className="text-white font-bold bg-black/50 px-3 py-1 rounded">
                          COLLECT: {formatCurrency(collectableAmount, packingSlipData.currency || order?.currency)}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })()}
              
              {/* Driver Info */}
              {packingSlipData.driver_name && (
                <div className="bg-blue-500/20 border border-blue-500/50 rounded-lg p-3">
                  <span className="text-blue-400">
                    <strong>Assigned Driver:</strong> {packingSlipData.driver_name}
                  </span>
                </div>
              )}
              
              {/* Ship To Section */}
              <div className="bg-wt-bg rounded-lg border border-wt-border p-4">
                <h4 className="text-sm font-medium text-wt-text-muted mb-2">SHIP TO</h4>
                <div className="text-sm space-y-1">
                  <p className="font-medium">{packingSlipData.shipping_address?.name || packingSlipData.customer?.name}</p>
                  {/* Show full address string if available */}
                  {packingSlipData.shipping_address?.full_address ? (
                    <p className="whitespace-pre-line">{packingSlipData.shipping_address.full_address}</p>
                  ) : (
                    <>
                      {packingSlipData.shipping_address?.address1 && <p>{packingSlipData.shipping_address.address1}</p>}
                      {packingSlipData.shipping_address?.address2 && <p>{packingSlipData.shipping_address.address2}</p>}
                      <p>
                        {[packingSlipData.shipping_address?.city, packingSlipData.shipping_address?.province, packingSlipData.shipping_address?.zip].filter(Boolean).join(', ')}
                      </p>
                      {packingSlipData.shipping_address?.country && <p>{packingSlipData.shipping_address.country}</p>}
                    </>
                  )}
                  <p className="text-wt-text-muted">
                    <strong>Phone:</strong> {packingSlipData.shipping_address?.phone || packingSlipData.customer?.phone || 'N/A'}
                  </p>
                </div>
              </div>
              
              {/* Items Table */}
              <div className="bg-wt-bg rounded-lg border border-wt-border overflow-hidden">
                <div className="bg-wt-paper px-4 py-2 border-b border-wt-border">
                  <h4 className="text-sm font-medium text-wt-text-muted">
                    ITEMS ({packingSlipData.item_count} items, {packingSlipData.total_quantity} units)
                  </h4>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-wt-paper/50">
                    <tr>
                      <th className="px-3 py-2 text-left text-wt-text-muted font-medium">RACK</th>
                      <th className="px-3 py-2 text-left text-wt-text-muted font-medium">ITEM</th>
                      <th className="px-3 py-2 text-left text-wt-text-muted font-medium">SKU</th>
                      <th className="px-3 py-2 text-center text-wt-text-muted font-medium">QTY</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-wt-border">
                    {packingSlipData.items?.map((item, idx) => (
                      <tr key={idx} className="hover:bg-wt-paper/30">
                        <td className="px-3 py-2">
                          {item.rack_number ? (
                            <Badge className="bg-amber-500/20 text-amber-400 font-mono">{item.rack_number}</Badge>
                          ) : (
                            <span className="text-wt-text-muted">-</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            {(item.image || item.image_url) && (
                              <img 
                                src={item.image || item.image_url} 
                                alt="" 
                                className="w-10 h-10 object-cover rounded border border-wt-border"
                                onError={(e) => e.target.style.display = 'none'}
                              />
                            )}
                            <div>
                              <span className="text-wt-text-primary">{item.name || item.title}</span>
                              {item.variant_title && (
                                <span className="text-wt-text-muted text-xs block">{item.variant_title}</span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-wt-text-muted">{item.sku || '-'}</td>
                        <td className="px-3 py-2 text-center font-bold">{item.quantity || 1}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Notes */}
              {packingSlipData.notes && (
                <div className="bg-wt-bg rounded-lg border border-dashed border-wt-border p-3">
                  <p className="text-sm"><strong>Notes:</strong> {packingSlipData.notes}</p>
                </div>
              )}
              
              {/* Print Button */}
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setPackingSlipOpen(false)}
                  className="border-wt-border text-wt-text-muted"
                >
                  Close
                </Button>
                <Button
                  onClick={printPackingSlip}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {createPortal(
      <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={onClose}
          />
          
          {/* Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 h-full w-full max-w-3xl bg-wt-bg border-l border-wt-border z-50 overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="flex-shrink-0 bg-wt-paper border-b border-wt-border p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-xl sm:text-2xl font-bold text-wt-text-primary truncate">
                      {order.source === 'danube' ? order.order_number : (order.shopify_order_name || order.order_number)}
                    </h2>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Badge className={`${status.color} text-white text-xs`}>
                        {status.label}
                      </Badge>
                      <Badge className={`border ${financialStatus.color} text-xs`}>
                        {financialStatus.label}
                      </Badge>
                      {order.mamopay_paid && (
                        <Badge className="bg-green-500/20 text-green-400 border border-green-500/30 text-xs">
                          <CreditCard className="w-3 h-3 mr-1" />
                          MamoPay
                        </Badge>
                      )}
                      {(() => {
                        const displayed = order.source === 'danube' ? order.order_number : (order.shopify_order_name || order.order_number);
                        const secondary = order.shopify_order_name && order.shopify_order_name !== displayed
                          ? { label: 'Shopify', value: order.shopify_order_name }
                          : (order.order_number && order.order_number !== displayed
                            ? { label: 'Order', value: order.order_number }
                            : null);
                        return secondary ? (
                          <Badge className="bg-wt-subtle text-wt-text-secondary border border-wt-border text-xs" data-testid="secondary-order-number">
                            <Store className="w-3 h-3 mr-1" />
                            {secondary.label}: {secondary.value}
                          </Badge>
                        ) : null;
                      })()}
                      {order.has_marketplace_vendor_items && (
                        <Badge
                          className="bg-violet-500/20 text-violet-300 border border-violet-500/40 text-xs"
                          data-testid="order-mp-vendor-badge"
                          title="This order contains items fulfilled by a marketplace vendor"
                        >
                          <Store className="w-3 h-3 mr-1" />
                          Marketplace Vendor
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-xs sm:text-sm text-wt-text-muted flex-wrap">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                      {formatDate(order.created_at_shopify || order.created_at)}
                    </span>
                    {/* Source badge — one row, every channel. Theme/colour
                        come from /app/frontend/src/lib/channels.js so this
                        renderer stays in sync with the Orders list pill,
                        Reports, Dashboard, Returns, etc. */}
                    {(() => {
                      const slug = detectChannel(order);
                      if (!slug) return null;
                      const meta = getChannelMeta(slug, order);
                      return (
                        <span
                          className={`flex items-center gap-1 ${meta.textCls}`}
                          data-testid={`${slug}-source-badge`}
                        >
                          <ShoppingBag className="w-3 h-3 sm:w-4 sm:h-4" />
                          {meta.label}
                        </span>
                      );
                    })()}
                  </div>
                  {(order.fulfillment_status === 'delivery_rescheduled' || (order.delivery_attempts || []).length > 0) && (
                    <div
                      data-testid="delivery-reschedule-banner"
                      className={`mt-3 p-3 rounded-lg ${order.fulfillment_status === 'delivery_rescheduled' ? 'bg-amber-500/10 border border-amber-500/40' : 'bg-wt-bg/40 border border-wt-border'}`}
                    >
                      <div className="flex items-start gap-2">
                        <Clock className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-amber-300">
                            {order.fulfillment_status === 'delivery_rescheduled'
                              ? `Delivery rescheduled — attempt ${order.delivery_attempt_count || 1} of 3`
                              : `Delivery attempts: ${(order.delivery_attempts || []).length}`}
                          </p>
                          <ul className="mt-1 text-xs text-amber-200/80 space-y-0.5">
                            {(order.delivery_attempts || []).slice().reverse().map((a, i) => (
                              <li key={i} data-testid={`reschedule-attempt-${i}`}>
                                #{a.attempt_number}: {a.reason_label || (a.reason || '').replace(/_/g, ' ') || '—'}
                                {a.reason_note ? ` — ${a.reason_note}` : ''}
                                {a.preferred_date ? ` · asked for ${a.preferred_date}` : ''}
                                {a.driver_name ? ` · ${a.driver_name}` : ''} · {formatDate(a.at)}
                              </li>
                            ))}
                          </ul>
                          {order.fulfillment_status === 'delivery_rescheduled' && (
                            <>
                            <div className="mt-2 flex items-center gap-2 flex-wrap">
                              <span className="text-xs text-wt-text-secondary">New delivery date:</span>
                              <DateInput
                                value={reschedDateInput || order.rescheduled_delivery_date || ''}
                                onChange={(e) => setReschedDateInput(e.target.value)}
                                className="px-2 py-1 rounded bg-wt-bg border border-wt-border text-xs text-wt-text-primary"
                                data-testid="reschedule-date-picker"
                              />
                              <Button
                                size="sm"
                                onClick={handleSaveRescheduleDate}
                                disabled={savingReschedDate || !reschedDateInput}
                                data-testid="save-reschedule-date-btn"
                                className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/40 h-7 px-2 text-xs"
                              >
                                {savingReschedDate ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save Date'}
                              </Button>
                              {order.rescheduled_delivery_date && (
                                <span className="text-xs text-amber-300 font-semibold" data-testid="current-reschedule-date">
                                  Current: {order.rescheduled_delivery_date}
                                </span>
                              )}
                            </div>
                            <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="reschedule-reassign-row">
                              <span className="text-xs text-wt-text-secondary">
                                Driver: <span className="text-wt-text-primary font-medium">{order.assigned_driver_name || 'Unassigned'}</span>
                              </span>
                              {!showReassignDriver ? (
                                <Button
                                  size="sm"
                                  onClick={openReassignDriver}
                                  data-testid="reassign-driver-btn"
                                  className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/40 h-7 px-2 text-xs"
                                >
                                  Reassign Driver
                                </Button>
                              ) : (
                                <>
                                  <select
                                    value={reassignDriverId}
                                    onChange={(e) => setReassignDriverId(e.target.value)}
                                    className="px-2 py-1 rounded bg-wt-bg border border-wt-border text-xs text-wt-text-primary"
                                    data-testid="reassign-driver-select"
                                  >
                                    <option value="">Select driver...</option>
                                    {reassignDrivers.filter(d => d.id !== order.assigned_driver_id).map(d => (
                                      <option key={d.id} value={d.id}>{d.full_name}</option>
                                    ))}
                                  </select>
                                  <Button
                                    size="sm"
                                    onClick={handleReassignDriver}
                                    disabled={reassigningDriver || !reassignDriverId}
                                    data-testid="confirm-reassign-driver-btn"
                                    className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/40 h-7 px-2 text-xs"
                                  >
                                    {reassigningDriver ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Confirm'}
                                  </Button>
                                  <button
                                    onClick={() => { setShowReassignDriver(false); setReassignDriverId(''); }}
                                    className="text-xs text-wt-text-muted underline"
                                    data-testid="cancel-reassign-driver-btn"
                                  >
                                    Cancel
                                  </button>
                                </>
                              )}
                            </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  {order.shopify_edit_alert && !order.shopify_edit_alert.acknowledged && (
                    <div data-testid="shopify-edit-alert-banner" className="mt-3 p-3 rounded-lg bg-red-500/10 border border-red-500/40">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-red-300">
                            Edited on Shopify after packing — recheck items
                          </p>
                          <ul className="mt-1 text-xs text-red-200/80 list-disc list-inside space-y-0.5">
                            {(order.shopify_edit_alert.changes || []).map((c, i) => (
                              <li key={i} data-testid={`edit-alert-change-${i}`}>{c}</li>
                            ))}
                          </ul>
                          <p className="text-[11px] text-wt-text-muted mt-1.5">
                            Edited {formatDate(order.shopify_edit_alert.edited_at)} · was {String(order.shopify_edit_alert.order_stage || '').replace(/_/g, ' ')}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          onClick={handleAcknowledgeEditAlert}
                          disabled={ackingEditAlert}
                          data-testid="acknowledge-edit-alert-btn"
                          className="bg-red-500/20 hover:bg-red-500/30 text-red-200 border border-red-500/40 flex-shrink-0"
                        >
                          {ackingEditAlert ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5 mr-1" />}
                          Rechecked
                        </Button>
                      </div>
                    </div>
                  )}
                  {/* Show linked Purchase Orders - Clickable */}
                  {(order.linked_po_numbers?.length > 0 || order.linked_po_ids?.length > 0) && (
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <span className="text-xs text-wt-text-muted">Linked POs:</span>
                      <div className="flex flex-wrap gap-1">
                        {order.linked_po_numbers?.length > 0 ? (
                          // Show clickable PO number badges
                          order.linked_po_numbers.map((poNum, idx) => (
                            <Badge 
                              key={idx} 
                              className="bg-purple-500/20 text-purple-400 border-0 text-xs cursor-pointer hover:bg-purple-500/30 transition-colors"
                              onClick={() => {
                                onClose?.();
                                navigate(`/purchase-orders?poNumber=${encodeURIComponent(poNum)}`);
                              }}
                            >
                              {poNum}
                              <ExternalLink className="h-3 w-3 ml-1" />
                            </Badge>
                          ))
                        ) : (
                          // Fallback: Show popover with PO IDs if no numbers stored
                          <Popover>
                            <PopoverTrigger asChild>
                              <Badge className="bg-purple-500/20 text-purple-400 border-0 text-xs cursor-pointer hover:bg-purple-500/30">
                                {order.linked_po_ids.length} PO(s)
                                <ChevronRight className="h-3 w-3 ml-1" />
                              </Badge>
                            </PopoverTrigger>
                            <PopoverContent className="w-48 p-2 bg-wt-paper border-wt-border">
                              <p className="text-xs text-wt-text-muted mb-2">Linked Purchase Orders:</p>
                              <div className="space-y-1">
                                {order.linked_po_ids.map((poId, idx) => (
                                  <Button
                                    key={idx}
                                    variant="ghost"
                                    size="sm"
                                    className="w-full justify-start text-xs text-wt-text-primary hover:text-purple-400"
                                    onClick={() => {
                                      onClose?.();
                                      navigate(`/purchase-orders?po=${poId}`);
                                    }}
                                  >
                                    <Package className="h-3 w-3 mr-2" />
                                    View PO {idx + 1}
                                  </Button>
                                ))}
                              </div>
                            </PopoverContent>
                          </Popover>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {/* Close button - always visible */}
                <Button variant="ghost" size="icon" onClick={onClose} className="flex-shrink-0 ml-2">
                  <X className="w-5 h-5 text-wt-text-muted" />
                </Button>
              </div>

              {/* Cancellation Notice */}
              {isCancelled && (
                <div className="bg-gray-500/10 border border-gray-500/30 rounded-lg p-3 mt-3">
                  <div className="flex items-center gap-2 text-gray-400">
                    <XCircle className="w-4 h-4" />
                    <span className="font-medium">Order Cancelled</span>
                  </div>
                  {(order.cancellation_reason || order.cancel_reason) && (
                    <p className="text-sm text-gray-500 mt-1">Reason: {order.cancellation_reason || order.cancel_reason}</p>
                  )}
                  {order.cancelled_by_name && (
                    <p className="text-xs text-gray-500 mt-1">Cancelled by: {order.cancelled_by_name}</p>
                  )}
                  {order.cancelled_at && (
                    <p className="text-xs text-gray-500 mt-1">Cancelled at: {formatDate(order.cancelled_at)}</p>
                  )}
                </div>
              )}

              {/* Show invoice badge if already created */}
              {order.zoho_invoice_id && (
                <div className="mt-3">
                  <Badge className="bg-green-500/20 text-green-400 border border-green-500/30 text-xs h-8 flex items-center">
                    <FileText className="w-3 h-3 mr-1" />
                    Invoice: {order.zoho_invoice_number}
                  </Badge>
                </div>
              )}

              {/* Incentive Status Badge */}
              {canRequestIncentive && incentiveSettings?.is_active && incentiveStatus?.requested && (
                <div className="mt-3">
                  <Badge 
                    className={`${
                      incentiveStatus.status === 'approved' 
                        ? 'bg-green-500/20 text-green-400 border-green-500/30'
                        : incentiveStatus.status === 'rejected'
                        ? 'bg-red-500/20 text-red-400 border-red-500/30'
                        : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                    } border text-xs h-8 flex items-center`}
                    title={`Requested by: ${incentiveStatus.requested_by}`}
                  >
                    <DollarSign className="w-3 h-3 mr-1" />
                    Incentive {incentiveStatus.status}: {formatCurrency(incentiveStatus.amount)}
                  </Badge>
                </div>
              )}

              {/* Slip printed audit line — who printed it and when */}
              {order.packing_slip_printed && (
                <div className="flex items-center gap-1.5 mt-2 text-xs text-cyan-300" data-testid="slip-printed-info">
                  <Printer className="h-3.5 w-3.5" />
                  <span>
                    Slip printed
                    {order.packing_slip_printed_by_name ? <> by <span className="font-medium text-cyan-200">{order.packing_slip_printed_by_name}</span></> : null}
                    {order.packing_slip_printed_at ? <span className="text-wt-text-muted"> · {new Date(order.packing_slip_printed_at).toLocaleString()}</span> : null}
                  </span>
                </div>
              )}

              {/* Compact Action Bar - Packing Slip + Actions Dropdown */}
              <div className="flex items-center gap-2 mt-3">
                {/* Print Packing Slip Button - Always visible */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrintPackingSlip}
                  disabled={loadingPackingSlip}
                  className={`${order.packing_slip_printed ? 'border-amber-500/50 text-amber-400 hover:bg-amber-500/10' : 'border-green-500/50 text-green-400 hover:bg-green-500/10'} text-xs sm:text-sm h-8`}
                  title={order.packing_slip_printed ? `Packing slip was already printed${order.packing_slip_printed_by_name ? ` by ${order.packing_slip_printed_by_name}` : ''}${order.packing_slip_printed_at ? ` on ${new Date(order.packing_slip_printed_at).toLocaleString()}` : ''}` : "Print packing slip with rack locations"}
                  data-testid="packing-slip-btn"
                >
                  <Printer className={`w-3 h-3 sm:w-4 sm:h-4 mr-1 ${loadingPackingSlip ? 'animate-spin' : ''}`} />
                  {loadingPackingSlip ? 'Loading...' : (order.packing_slip_printed ? 'Reprint Slip' : 'Packing Slip')}
                </Button>

                {/* Actions Dropdown - Contains all other actions */}
                {!isCancelled && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-wt-border text-wt-text-primary hover:bg-wt-subtle text-xs sm:text-sm h-8"
                        data-testid="order-actions-dropdown"
                      >
                        <MoreHorizontal className="w-4 h-4 mr-1" />
                        Actions
                        <ChevronDown className="w-3 h-3 ml-1" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent 
                      align="start" 
                      className="w-56 bg-wt-paper border-wt-border"
                    >
                      {/* Shopify Sync Actions */}
                      {(order.source === 'shopify' || order.shopify_order_id) && canSyncShopify && (
                        <>
                          <DropdownMenuItem
                            onClick={handleSyncFromShopify}
                            disabled={syncing || !order.shopify_order_id}
                            className="text-wt-text-primary hover:bg-wt-subtle cursor-pointer"
                          >
                            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                            {syncing ? 'Syncing...' : 'Sync from Shopify'}
                          </DropdownMenuItem>
                          {/* `Refresh address from Shopify` removed
                              — `Sync from Shopify` already pulls
                              addresses (and everything else) from
                              Shopify in one shot. Keeping two buttons
                              that do overlapping things confused
                              operators when one failed but the other
                              didn't. */}
                          {['picked', 'on_the_way', 'delivered', 'failed'].includes(order.fulfillment_status) && order.shopify_order_id && (
                            <DropdownMenuItem
                              onClick={handleSyncFulfillmentToShopify}
                              disabled={syncingToShopify}
                              className="text-blue-400 hover:bg-blue-500/10 cursor-pointer"
                            >
                              <Globe className={`w-4 h-4 mr-2 ${syncingToShopify ? 'animate-spin' : ''}`} />
                              {syncingToShopify ? 'Pushing...' : 'Push to Shopify'}
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator className="bg-wt-border" />
                        </>
                      )}

                      {/* Re-push channel orders (Danube / Noon / Home Centre / Carrefour) to Shopify */}
                      {['danube', 'noon', 'homecentre', 'carrefour'].includes(order.source) && canSyncShopify && order.status !== 'cancelled' && (
                        <DropdownMenuItem
                          onClick={async () => {
                            const isRepush = !!order.shopify_order_id;
                            if (isRepush && !window.confirm('Re-push this order to Shopify?\n\nThe current Shopify order will be CANCELLED (items restocked) and a fresh one created with the correct products.')) return;
                            setPushingShopify(true);
                            try {
                              const token = localStorage.getItem('token');
                              const res = await axios.post(`${API_BASE_URL}/orders/${order.id}/push-to-shopify`,
                                { repush: isRepush }, { headers: { Authorization: `Bearer ${token}` } });
                              toast.success(`${isRepush ? 'Re-pushed' : 'Pushed'} to Shopify — ${res.data.shopify_order_name || 'done'}`);
                              if (onRefresh) {
                                const r2 = await axios.get(`${API_BASE_URL}/orders/${order.id}`, { headers: { Authorization: `Bearer ${token}` } });
                                onRefresh(r2.data);
                              }
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Shopify push failed');
                            } finally {
                              setPushingShopify(false);
                            }
                          }}
                          disabled={pushingShopify}
                          className="text-amber-400 hover:bg-amber-500/10 cursor-pointer"
                          data-testid="repush-to-shopify-menu-item"
                        >
                          <RefreshCw className={`w-4 h-4 mr-2 ${pushingShopify ? 'animate-spin' : ''}`} />
                          {pushingShopify ? 'Pushing…' : (order.shopify_order_id ? 'Re-push to Shopify' : 'Push to Shopify')}
                        </DropdownMenuItem>
                      )}

                      {/* Edit Order */}
                      {(isAdmin || canEditOrder) && (
                        <DropdownMenuItem
                          onClick={() => setEditOrderOpen(true)}
                          className="text-wt-text-primary hover:bg-wt-subtle cursor-pointer"
                          data-testid="edit-order-menu-item"
                        >
                          <Edit2 className="w-4 h-4 mr-2" />
                          Edit Order
                        </DropdownMenuItem>
                      )}

                      {/* Create PO */}
                      {(isAdmin || canCreatePO) && (
                        <DropdownMenuItem
                          onClick={() => onCreatePO && onCreatePO(order)}
                          className="text-wt-gold hover:bg-wt-gold/10 cursor-pointer"
                        >
                          <Package className="w-4 h-4 mr-2" />
                          Create PO
                        </DropdownMenuItem>
                      )}

                      {/* Create Return */}
                      {!order.has_return && (
                        <DropdownMenuItem
                          onClick={openCreateReturnDialog}
                          className="text-purple-400 hover:bg-purple-500/10 cursor-pointer"
                          data-testid="create-return-action"
                        >
                          <RotateCcw className="w-4 h-4 mr-2" />
                          Create Return
                        </DropdownMenuItem>
                      )}

                      {/* Share Order Status / Tracking Link */}
                      {order.tracking_token && (
                        <DropdownMenuItem
                          onClick={() => {
                            const trackingUrl = `${window.location.origin}/track/${order.tracking_token}`;
                            navigator.clipboard.writeText(trackingUrl);
                            toast.success('Tracking link copied to clipboard!');
                          }}
                          className="text-blue-400 hover:bg-blue-500/10 cursor-pointer"
                        >
                          <Share2 className="w-4 h-4 mr-2" />
                          Share Order Status
                        </DropdownMenuItem>
                      )}
                      {!order.tracking_token && (
                        <DropdownMenuItem
                          onClick={async () => {
                            try {
                              const response = await fetch(`${API_BASE_URL}/tracking/generate-token/${order.id}`, {
                                method: 'POST',
                                headers: {
                                  'Authorization': `Bearer ${localStorage.getItem('token')}`,
                                  'Content-Type': 'application/json'
                                }
                              });
                              if (response.ok) {
                                const data = await response.json();
                                navigator.clipboard.writeText(data.tracking_url);
                                toast.success('Tracking link generated and copied!');
                                onRefresh && onRefresh(order);
                              } else {
                                toast.error('Failed to generate tracking link');
                              }
                            } catch (err) {
                              toast.error('Failed to generate tracking link');
                            }
                          }}
                          className="text-blue-400 hover:bg-blue-500/10 cursor-pointer"
                        >
                          <Link className="w-4 h-4 mr-2" />
                          Generate Tracking Link
                        </DropdownMenuItem>
                      )}

                      {/* Create Invoice */}
                      {(isAdmin || canCreateInvoice) && !order.zoho_invoice_id && (
                        <DropdownMenuItem
                          onClick={handleCreateZohoInvoice}
                          disabled={creatingInvoice}
                          className="text-green-400 hover:bg-green-500/10 cursor-pointer"
                        >
                          <FileText className={`w-4 h-4 mr-2 ${creatingInvoice ? 'animate-spin' : ''}`} />
                          {creatingInvoice ? 'Creating...' : 'Create Invoice'}
                        </DropdownMenuItem>
                      )}

                      {/* Mark Ready for Dispatch */}
                      {(isAdmin || canMarkReadyForDispatch) && 
                       ['pending', 'unfulfilled', 'packed', null, undefined, ''].includes(order.fulfillment_status) && 
                       order.fulfillment_status !== 'ready_for_dispatch' && (
                        <DropdownMenuItem
                          onClick={handleMarkReadyForDispatch}
                          disabled={markingReadyForDispatch}
                          className="text-cyan-400 hover:bg-cyan-500/10 cursor-pointer"
                        >
                          <Package className={`w-4 h-4 mr-2 ${markingReadyForDispatch ? 'animate-spin' : ''}`} />
                          {markingReadyForDispatch ? 'Updating...' : 'Ready for Dispatch'}
                        </DropdownMenuItem>
                      )}

                      {/* Payment Link */}
                      {(isAdmin || canCreatePaymentLink) && (
                        <PaymentLinkButton
                          referenceType="order"
                          referenceId={order.id}
                          amount={(() => {
                            const total = Number(order.total_amount ?? order.total_price ?? 0);
                            const due = order.amount_due != null ? Number(order.amount_due) : total - Number(order.paid_amount || 0);
                            return Math.max(0, Math.round((due || total) * 100) / 100);
                          })()}
                          customerName={`${order.shipping_address?.first_name || ''} ${order.shipping_address?.last_name || ''}`.trim() || order.customer?.name}
                          customerEmail={order.customer?.email}
                          customerPhone={order.shipping_address?.phone || order.customer?.phone}
                          title={`Order ${order.order_number || order.id}`}
                          existingLinks={order.payment_links || []}
                          onLinkCreated={() => onRefresh && onRefresh(order)}
                          onPaymentConfirmed={() => onRefresh && onRefresh(order)}
                          compact={true}
                          asDropdownItem={true}
                        />
                      )}

                      {/* Request Incentive */}
                      {canRequestIncentive && incentiveSettings?.is_active && !incentiveStatus?.requested && (
                        <DropdownMenuItem
                          onClick={() => {
                            if (incentiveStatus?.employee_eligible === false) {
                              if (!window.confirm(`${incentiveStatus.employee_eligibility_reason || 'Incentives are disabled for this employee.'}\n\nSubmit anyway? (Admin override will be required)`)) return;
                            }
                            handleRequestIncentive();
                          }}
                          disabled={requestingIncentive}
                          className="text-emerald-400 hover:bg-emerald-500/10 cursor-pointer"
                          data-testid="request-incentive-menu-item"
                        >
                          {requestingIncentive ? (
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <DollarSign className="w-4 h-4 mr-2" />
                          )}
                          Request Incentive
                          {incentiveStatus?.employee_eligible === false && (
                            <AlertTriangle className="w-3 h-3 ml-1.5 text-amber-400" />
                          )}
                        </DropdownMenuItem>
                      )}

                      {(isAdmin || (order.source === 'shopify' && canSyncShopify) || canRequestIncentive) && (
                        <DropdownMenuSeparator className="bg-wt-border" />
                      )}

                      {/* View/Upload POD */}
                      {(isAdmin || canAssignDriver) && order.assigned_driver_id && 
                       ['picked', 'on_the_way', 'delivered'].includes(order.fulfillment_status) && (
                        <DropdownMenuItem
                          onClick={() => onViewPOD && onViewPOD(order)}
                          className={`${order.proof_of_delivery_url ? 'text-green-400 hover:bg-green-500/10' : 'text-blue-400 hover:bg-blue-500/10'} cursor-pointer`}
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          {order.proof_of_delivery_url ? 'View POD' : 'Upload POD'}
                        </DropdownMenuItem>
                      )}

                      {/* Assign Driver */}
                      {canAssignDriver && !isFulfilled && !isFailed && !order.assigned_driver_id && (
                        <DropdownMenuItem
                          onClick={() => onAssignDriver && onAssignDriver(order)}
                          className="text-blue-400 hover:bg-blue-500/10 cursor-pointer"
                        >
                          <Truck className="w-4 h-4 mr-2" />
                          Assign Driver
                        </DropdownMenuItem>
                      )}

                      {/* Reassign Driver - Show when driver is already assigned */}
                      {canAssignDriver && !isFulfilled && !isFailed && order.assigned_driver_id && (
                        <DropdownMenuItem
                          onClick={() => onAssignDriver && onAssignDriver(order)}
                          className="text-orange-400 hover:bg-orange-500/10 cursor-pointer"
                        >
                          <Truck className="w-4 h-4 mr-2" />
                          Reassign Driver
                        </DropdownMenuItem>
                      )}

                      {/* Unassign Driver - Show when driver is already assigned */}
                      {canAssignDriver && !isFulfilled && !isFailed && order.assigned_driver_id && (
                        <DropdownMenuItem
                          onClick={() => onUnassignDriver && onUnassignDriver(order)}
                          className="text-red-400 hover:bg-red-500/10 cursor-pointer"
                          data-testid="unassign-driver-btn"
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Unassign Driver
                        </DropdownMenuItem>
                      )}

                      {/* Book Courier - Show when no courier is assigned and order is not delivered/cancelled */}
                      {canBookCourier && !isFulfilled && !isFailed && !order.courier_awb && !order.awb_number &&
                       !['fulfilled', 'delivered', 'cancelled', 'refunded', 'booked', 'in_transit', 'out_for_delivery'].includes(order.fulfillment_status) && (
                        <DropdownMenuItem
                          onClick={() => onBookCourier && onBookCourier(order)}
                          className="text-red-400 hover:bg-red-500/10 cursor-pointer"
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Book Courier
                        </DropdownMenuItem>
                      )}

                      {/* View Courier Tracking - Show when courier is booked */}
                      {(order.courier_awb || order.awb_number) && (
                        <DropdownMenuItem
                          onClick={() => {
                            const awb = order.courier_awb || order.awb_number;
                            const courierType = (order.courier_type || '').toLowerCase();
                            let trackingUrl = '';
                            if (courierType === 'delhivery') {
                              trackingUrl = `https://www.delhivery.com/track/package/${awb}`;
                            } else if (courierType === 'bluedart') {
                              trackingUrl = `https://www.bluedart.com/tracking/${awb}`;
                            } else if (courierType === 'dtdc') {
                              trackingUrl = `https://www.dtdc.in/tracking.asp?strCnno=${awb}`;
                            } else {
                              trackingUrl = `https://www.delhivery.com/track/package/${awb}`;
                            }
                            window.open(trackingUrl, '_blank');
                          }}
                          className="text-blue-400 hover:bg-blue-500/10 cursor-pointer"
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Track Courier ({order.courier_type || 'Courier'})
                        </DropdownMenuItem>
                      )}

                      {/* Unassign Courier - Show when courier is assigned and not yet delivered.
                          For split orders, per-shipment unassign is used instead (see shipment cards below). */}
                      {canBookCourier && !order.is_split && (order.courier_awb || order.awb_number) && 
                       !['delivered', 'failed'].includes(order.status) && (
                        <DropdownMenuItem
                          onClick={() => onUnassignCourier && onUnassignCourier(order)}
                          className="text-orange-400 hover:bg-orange-500/10 cursor-pointer"
                          data-testid="unassign-courier-btn"
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Unassign Courier
                        </DropdownMenuItem>
                      )}

                      {/* Override Status - For admins on any order */}
                      {canOverrideStatus && (
                        <DropdownMenuItem
                          onClick={() => onOverrideStatus && onOverrideStatus(order)}
                          className="text-purple-400 hover:bg-purple-500/10 cursor-pointer"
                        >
                          <History className="w-4 h-4 mr-2" />
                          Override Status
                        </DropdownMenuItem>
                      )}

                      {/* Split Shipment - Show for orders that can be split */}
                      {canSplit && !order.is_split && (
                        <DropdownMenuItem
                          onClick={initializeSplitShipments}
                          className="text-indigo-400 hover:bg-indigo-500/10 cursor-pointer"
                        >
                          <GitBranch className="w-4 h-4 mr-2" />
                          Split Shipment
                        </DropdownMenuItem>
                      )}

                      {/* Reopen Order */}
                      {canManageReturns && (isFulfilled || isFailed) && (
                        <DropdownMenuItem
                          onClick={() => onReopen && onReopen(order)}
                          className="text-amber-400 hover:bg-amber-500/10 cursor-pointer"
                        >
                          <RotateCcw className="w-4 h-4 mr-2" />
                          Reopen Order
                        </DropdownMenuItem>
                      )}

                      {/* Legacy Return/Replacement removed — use "Create Return"
                          (returns module) which covers refunds & replacements */}

                      {/* Refund */}
                      {canManageRefunds && order.return_replacement_type && (
                        <DropdownMenuItem
                          onClick={() => onRefund && onRefund(order)}
                          className="text-green-400 hover:bg-green-500/10 cursor-pointer"
                        >
                          <DollarSign className="w-4 h-4 mr-2" />
                          Manage Refund
                        </DropdownMenuItem>
                      )}

                      {/* Issue Store Credit (manual recovery) */}
                      {isAdmin && (
                        <DropdownMenuItem
                          onClick={openIssueCreditDialog}
                          className="text-emerald-400 hover:bg-emerald-500/10 cursor-pointer"
                          data-testid="issue-store-credit-btn"
                        >
                          <CreditCard className="w-4 h-4 mr-2" />
                          Issue Store Credit
                        </DropdownMenuItem>
                      )}

                      {/* Set Delivery Date */}
                      {order.fulfillment_status !== 'delivered' && (
                        <DropdownMenuItem
                          onClick={() => { setDeliveryDateInput(order.rescheduled_delivery_date || ''); setDeliveryDateOpen(true); }}
                          className="text-wt-text-primary hover:bg-wt-subtle cursor-pointer"
                          data-testid="set-delivery-date-menu-item"
                        >
                          <CalendarIcon className="w-4 h-4 mr-2" />
                          Set Delivery Date
                        </DropdownMenuItem>
                      )}

                      {/* Cancel Order */}
                      {(isAdmin || canCancelOrder) && order.fulfillment_status !== 'delivered' && (
                        <>
                          <DropdownMenuSeparator className="bg-wt-border" />
                          <DropdownMenuItem
                            onClick={() => setCancelOrderOpen(true)}
                            className="text-red-400 hover:bg-red-500/10 cursor-pointer"
                            data-testid="cancel-order-menu-item"
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Cancel Order
                          </DropdownMenuItem>
                        </>
                      )}

                      {/* Audit Log */}
                      {(order.is_reopened || order.return_replacement_type) && (
                        <>
                          <DropdownMenuSeparator className="bg-wt-border" />
                          <DropdownMenuItem
                            onClick={() => onViewAuditLog && onViewAuditLog(order)}
                            className="text-wt-text-muted hover:bg-wt-subtle cursor-pointer"
                          >
                            <History className="w-4 h-4 mr-2" />
                            View Audit Log
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>

            {/* Tabs - scrollable on mobile */}
            <div className="flex-shrink-0 bg-wt-paper border-b border-wt-border px-2 sm:px-4 overflow-x-auto">
              <div className="flex gap-0.5 sm:gap-1 min-w-max">
                {['details', 'items', 'customer', 'timeline'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium capitalize transition-colors border-b-2 whitespace-nowrap ${
                      activeTab === tab
                        ? 'text-wt-gold border-wt-gold'
                        : 'text-wt-text-muted border-transparent hover:text-wt-text-primary'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>

            {/* Content */}
            <div ref={contentRef} className="flex-1 overflow-y-auto p-4 space-y-6">
              {activeTab === 'details' && (
                <>
                  {/* Express status + re-check */}
                  <div className="bg-wt-paper rounded-lg border border-wt-border p-3 flex items-center justify-between gap-3" data-testid="express-status-card">
                    <div className="flex items-center gap-2 text-sm">
                      <Zap className={`w-4 h-4 ${order.express?.enabled ? 'text-wt-gold' : 'text-wt-text-muted'}`} />
                      {order.express?.enabled ? (
                        <span className="text-wt-gold font-semibold">EXPRESS — due {order.express.deadline_at ? new Date(order.express.deadline_at).toLocaleString() : ''}{order.express.warehouse_name ? ` · ${order.express.warehouse_name}` : ''}</span>
                      ) : (
                        <span className="text-wt-text-muted">
                          Not an express order
                          {order.express?.reason === 'no_warehouse_city_match' && ` — no express warehouse covers "${(order.shipping_address?.city || order.express?.city || '')}"`}
                          {order.express?.reason === 'items_not_eligible' && ` — item(s) not express-eligible: ${(order.express.ineligible_skus || []).slice(0, 3).join(', ')}`}
                          {order.express?.reason === 'items_missing_sku' && ' — some items have no SKU'}
                        </span>
                      )}
                    </div>
                    <Button size="sm" variant="outline" className="shrink-0" data-testid="express-recheck-btn"
                      onClick={async () => {
                        try {
                          const t = localStorage.getItem('token');
                          const r = await axios.post(`${API_BASE_URL}/express/orders/${order.id}/reevaluate`, {}, { headers: { Authorization: `Bearer ${t}` } });
                          const ev = r.data.express || {};
                          if (ev.enabled) {
                            toast.success(`Express ✓ — ${ev.warehouse_name || ''}, due ${ev.deadline_at ? new Date(ev.deadline_at).toLocaleString() : ''}`);
                          } else if (ev.reason === 'no_warehouse_city_match') {
                            const whs = (r.data.express_warehouses || []).map((w) => `${w.name}: ${w.cities.join('/')}`).join(' · ');
                            toast.warning(`Not express — order city "${r.data.order_city}" doesn't match any express warehouse${whs ? ` (${whs})` : ' (none enabled)'}`);
                          } else if (ev.reason === 'items_not_eligible') {
                            toast.warning(`Not express — items not express-eligible: ${(ev.ineligible_skus || []).join(', ')}`);
                          } else {
                            toast.info(`Not express — ${ev.reason || 'conditions not met'}`);
                          }
                          if (onRefresh) {
                            try {
                              const or = await axios.get(`${API_BASE_URL}/orders/${order.id}`, { headers: { Authorization: `Bearer ${t}` } });
                              onRefresh(or.data);
                            } catch { /* noop */ }
                          }
                        } catch (e) { toast.error(e.response?.data?.detail || 'Re-check failed'); }
                      }}>
                      Re-check Express
                    </Button>
                  </div>
                  {/* Flipkart Fulfillment actions — only for Flipkart orders */}
                  {order.source === 'flipkart' && (
                    <FlipkartFulfillmentActions
                      order={order}
                      onRefresh={async () => {
                        if (!onRefresh) return;
                        try {
                          const t = localStorage.getItem('token');
                          const r = await axios.get(
                            `${API_BASE_URL}/orders/${order.id}`,
                            { headers: { Authorization: `Bearer ${t}` } }
                          );
                          onRefresh(r.data);
                        } catch {
                          onRefresh(order);
                        }
                      }}
                    />
                  )}

                  {/* Amazon Fulfillment actions — for any amazon_* source orders */}
                  {order.source === 'amazon' && (
                    <AmazonFulfillmentActions
                      order={order}
                      onRefresh={async () => {
                        if (!onRefresh) return;
                        try {
                          const t = localStorage.getItem('token');
                          const r = await axios.get(
                            `${API_BASE_URL}/orders/${order.id}`,
                            { headers: { Authorization: `Bearer ${t}` } }
                          );
                          onRefresh(r.data);
                        } catch {
                          onRefresh(order);
                        }
                      }}
                    />
                  )}

                  {/* Sales Attribution */}
                  <SalesAttributionPanel
                    order={order}
                    isAdmin={isAdmin}
                    onRefresh={onRefresh}
                  />

                  {/* Payment Verification (manually marked paid orders) */}
                  {order.payment_verification && (
                    <div className={`rounded-lg border p-4 ${
                      order.payment_verification.status === 'approved' ? 'bg-green-500/10 border-green-500/30' :
                      order.payment_verification.status === 'rejected' ? 'bg-red-500/10 border-red-500/30' :
                      'bg-purple-500/10 border-purple-500/30'}`} data-testid="payment-verification-card">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <h3 className="text-sm font-medium text-wt-text-primary flex items-center gap-2">
                          <CreditCard className="w-4 h-4" /> Payment Verification
                        </h3>
                        <Badge className={
                          order.payment_verification.status === 'approved' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                          order.payment_verification.status === 'rejected' ? 'bg-red-500/20 text-red-400 border border-red-500/30' :
                          'bg-purple-500/20 text-purple-400 border border-purple-500/30'} data-testid="payment-verification-status-badge">
                          {order.payment_verification.status === 'approved' ? 'Approved' :
                           order.payment_verification.status === 'rejected' ? 'Rejected' : 'Pending Verification'}
                        </Badge>
                      </div>
                      <div className="mt-2 text-xs text-wt-text-muted space-y-1">
                        {order.payment_verification.payment_method_name && (
                          <p>Method: <span className="text-wt-text-primary">{order.payment_verification.payment_method_name}</span></p>
                        )}
                        {order.payment_verification.submitted_by_name && (
                          <p>Submitted by {order.payment_verification.submitted_by_name}</p>
                        )}
                        {order.payment_verification.approved_by_name && (
                          <p>Approved by {order.payment_verification.approved_by_name}</p>
                        )}
                        {order.payment_verification.proof_url && (
                          <a href={order.payment_verification.proof_url} target="_blank" rel="noreferrer"
                            className="text-wt-gold hover:underline inline-block" data-testid="view-payment-proof-link">
                            View payment proof →
                          </a>
                        )}
                        {order.payment_verification.status === 'rejected' && order.payment_verification.rejection_reason && (
                          <p className="text-red-400" data-testid="payment-rejection-reason">
                            Rejected{order.payment_verification.rejected_by_name ? ` by ${order.payment_verification.rejected_by_name}` : ''}: {order.payment_verification.rejection_reason}
                          </p>
                        )}
                      </div>
                      {order.payment_verification.status === 'rejected' && (
                        <div className="mt-3">
                          <label className="text-xs text-wt-text-primary block mb-1">Re-upload payment proof</label>
                          <input
                            type="file"
                            accept="image/*"
                            className="text-xs text-wt-text-muted"
                            data-testid="resubmit-proof-input"
                            onChange={async (e) => {
                              const f = e.target.files?.[0];
                              if (!f) return;
                              try {
                                const token = localStorage.getItem('token');
                                const fd = new FormData();
                                fd.append('file', f);
                                const up = await axios.post(`${API_BASE_URL}/orders/payment-proof/upload`, fd, { headers: { Authorization: `Bearer ${token}` } });
                                await axios.post(`${API_BASE_URL}/orders/${order.id}/payment-proof/resubmit`, { proof_url: up.data.proof_url }, { headers: { Authorization: `Bearer ${token}` } });
                                toast.success('Proof re-submitted for verification');
                                onRefresh && onRefresh({
                                  ...order,
                                  payment_verification: {
                                    ...order.payment_verification,
                                    status: 'pending',
                                    proof_url: up.data.proof_url,
                                  },
                                });
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Failed to re-submit proof');
                              }
                            }}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* noon Marketplace section */}
                  {order.source === 'noon' && order.noon && (
                    <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-4 space-y-2" data-testid="noon-order-card">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <h3 className="text-sm font-medium text-wt-text-primary flex items-center gap-2">
                          <Package className="w-4 h-4 text-yellow-400" /> noon Marketplace Order
                        </h3>
                        <div className="flex gap-2">
                          <Badge className="bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                            {{ own_truck: 'Our Truck', express: 'EXPRESS', noon_courier: 'noon Courier' }[order.noon.delivery_type] || order.noon.delivery_type}
                          </Badge>
                          {order.noon.shipment?.awb_nr && (
                            <Badge className={order.noon.manifested ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'} data-testid="noon-shipment-badge">
                              {order.noon.manifested ? 'Manifested' : 'Shipment created — manifest pending'}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-wt-text-muted">
                        noon order <span className="text-wt-text-primary">{order.noon.fbpi_order_nr}</span> · warehouse <span className="text-wt-text-primary">{order.noon.warehouse_code}</span>
                      </p>
                      {order.noon.shipment_error && !order.noon.shipment?.awb_nr && (
                        <p className="text-xs text-red-400" data-testid="noon-shipment-error">Auto-shipment failed: {order.noon.shipment_error}</p>
                      )}
                      {order.noon.shipment?.awb_nr ? (
                        <div className="text-xs text-wt-text-muted space-y-1">
                          <p>AWB: <span className="text-wt-gold font-mono">{order.noon.shipment.awb_nr}</span> · courier: {order.noon.shipment.courier} · by {order.noon.shipment.created_by_name}</p>
                          <Button size="sm" variant="outline" className="border-wt-border text-wt-text-primary h-7 text-xs"
                            data-testid="noon-refresh-order-btn"
                            onClick={async () => {
                              try {
                                const token = localStorage.getItem('token');
                                await axios.post(`${API_BASE_URL}/noon/orders/${order.id}/reprocess`, {}, { headers: { Authorization: `Bearer ${token}` } });
                                toast.success('Re-synced from noon — customer details updated');
                                try { onRefresh && onRefresh(); } catch (_) { /* refresh is best-effort */ }
                              } catch (err) { toast.error(err.response?.data?.detail || 'Failed to refresh'); }
                            }}>
                            <RefreshCw className="h-3 w-3 mr-1" />
                            Refresh from noon
                          </Button>
                          {!order.noon.manifested && (
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-amber-400">Paste the AWB QR on the packet, then create the manifest in noon Seller Lab.</p>
                              <Button size="sm" variant="outline" className="border-wt-border text-wt-text-primary h-7 text-xs"
                                data-testid="noon-mark-manifested-btn"
                                onClick={async () => {
                                  try {
                                    const token = localStorage.getItem('token');
                                    await axios.post(`${API_BASE_URL}/noon/orders/${order.id}/mark-manifested`, {}, { headers: { Authorization: `Bearer ${token}` } });
                                    toast.success('Marked as manifested');
                                    onRefresh && onRefresh({ ...order, noon: { ...order.noon, manifested: true } });
                                  } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                                }}>
                                Mark Manifested
                              </Button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <p className="text-xs text-wt-text-muted">Scan the shipment QR or type the AWB number. Leave blank to use the next AWB from the pool (noon courier).</p>
                          <div className="flex items-center gap-2 flex-wrap">
                            <input
                              type="text"
                              placeholder="Scan / enter AWB number (optional)"
                              className="flex-1 min-w-[200px] h-9 px-3 rounded-md bg-wt-bg border border-wt-border text-wt-text-primary text-sm"
                              data-testid="noon-awb-input"
                              id={`noon-awb-${order.id}`}
                            />
                            <Button size="sm" className="bg-wt-gold hover:bg-wt-gold-hover text-black"
                              data-testid="noon-create-shipment-btn"
                              onClick={async () => {
                                const awb = (document.getElementById(`noon-awb-${order.id}`)?.value || '').trim();
                                try {
                                  const token = localStorage.getItem('token');
                                  const r = await axios.post(`${API_BASE_URL}/noon/orders/${order.id}/create-shipment`, { awb_nr: awb || null }, { headers: { Authorization: `Bearer ${token}` } });
                                  toast.success(`Shipment created — AWB ${r.data.shipment.awb_nr}`);
                                  onRefresh && onRefresh({ ...order, noon: { ...order.noon, shipment: r.data.shipment }, fulfillment_status: 'packed' });
                                } catch (err) { toast.error(err.response?.data?.detail || 'Shipment creation failed'); }
                              }}>
                              Create noon Shipment
                            </Button>
                          </div>
                          {(order.items || []).some(i => (i.mp_item_nrs || []).length > 0) && (
                            <div className="text-xs text-wt-text-muted space-y-1">
                              {(order.items || []).map((i, ix) => (
                                <div key={ix} className="flex items-center justify-between gap-2">
                                  <span>{i.title} × {i.quantity}</span>
                                  <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/10 h-6 text-[11px]"
                                    data-testid={`noon-oos-${i.sku}`}
                                    onClick={async () => {
                                      if (!window.confirm(`Mark "${i.title}" as OUT OF STOCK on noon? This cannot be undone.`)) return;
                                      try {
                                        const token = localStorage.getItem('token');
                                        await axios.post(`${API_BASE_URL}/noon/orders/${order.id}/mark-oos`, { mp_item_nrs: i.mp_item_nrs }, { headers: { Authorization: `Bearer ${token}` } });
                                        toast.success('Marked out of stock on noon');
                                        onRefresh && onRefresh(order);
                                      } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                                    }}>
                                    Mark OOS
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Danube Home Marketplace section */}
                  {order.source === 'danube' && order.danube && (
                    <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-4 space-y-3" data-testid="danube-order-card">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <h3 className="text-sm font-medium text-wt-text-primary flex items-center gap-2">
                          <Package className="w-4 h-4 text-yellow-400" /> Danube Home Order
                        </h3>
                        <Badge className="bg-yellow-500/20 text-yellow-400 border border-yellow-500/30" data-testid="danube-status-badge">
                          {{ 0: 'Pending', 1: 'Accepted', 2: 'Ready to Pickup', 3: 'Shipped / Picked', 4: 'Delivered', 8: 'Completed', 9: 'Invoice Pending' }[order.danube.danube_status] ?? `Status ${order.danube.danube_status}`}
                        </Badge>
                      </div>
                      <p className="text-xs text-wt-text-muted">
                        Ref <span className="text-wt-text-primary">{order.danube.reference_no}</span>
                        {order.danube.lpo && <> · LPO <span className="text-wt-text-primary">{order.danube.lpo}</span></>}
                        {order.danube.deliver_by && <> · deliver by <span className="text-amber-400">{new Date(order.danube.deliver_by).toLocaleDateString()}</span></>}
                      </p>
                      <p className="text-xs text-wt-text-muted">
                        Payable (net of commission): <span className="text-wt-gold font-medium">{formatCurrency ? formatCurrency(order.danube.payable) : order.danube.payable} {order.currency}</span>
                        {order.danube.commission ? <> · commission {order.danube.commission}</> : null}
                      </p>
                      {order.danube.zoho_invoice_number && (
                        <p className="text-xs text-green-400" data-testid="danube-invoice-info">Zoho invoice {order.danube.zoho_invoice_number}{order.danube.invoice_submitted ? ' · submitted to Danube' : ''}</p>
                      )}
                      {order.shopify_order_name && (
                        <p className="text-xs text-wt-text-muted" data-testid="danube-shopify-number">Shopify order: <span className="text-green-400">{order.shopify_order_name}</span></p>
                      )}
                      {order.danube.shopify_sync_error && (
                        <p className="text-xs text-amber-400" data-testid="danube-shopify-sync-error">Shopify sync failed: {order.danube.shopify_sync_error}</p>
                      )}
                      {order.danube.status_push_error && (
                        <p className="text-xs text-red-400" data-testid="danube-status-push-error">Danube status push failed: {order.danube.status_push_error}</p>
                      )}

                      <DanubeActions order={order} onRefresh={onRefresh} />
                    </div>
                  )}

                  {/* Carrefour UAE Marketplace section */}
                  {order.source === 'carrefour' && order.carrefour && (
                    <div className="rounded-lg border border-blue-500/30 bg-blue-500/5 p-4 space-y-3" data-testid="carrefour-order-card">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <h3 className="text-sm font-medium text-wt-text-primary flex items-center gap-2">
                          <Package className="w-4 h-4 text-blue-400" /> Carrefour UAE Order
                        </h3>
                        <Badge className="bg-blue-500/20 text-blue-400 border border-blue-500/30" data-testid="carrefour-status-badge">
                          {{ new: 'New', packed: 'Packed', shipped: 'Shipped', delivered: 'Delivered', delivery_failed: 'Delivery Failed', cancelled: 'Cancelled' }[order.carrefour.status] ?? order.carrefour.status}
                        </Badge>
                      </div>
                      <p className="text-xs text-wt-text-muted">
                        Order <span className="text-wt-text-primary">{order.carrefour.order_code}</span>
                        {order.carrefour.consignment_code ? <> · consignment <span className="text-wt-text-primary">{order.carrefour.consignment_code}</span></> : null}
                        {order.carrefour.awb ? <> · AWB <span className="text-blue-400">{order.carrefour.awb}</span></> : null}
                      </p>
                      {order.carrefour.return_status && (
                        <p className="text-xs text-amber-400" data-testid="carrefour-return-status">Return: {order.carrefour.return_status}</p>
                      )}
                      {order.shopify_order_name && (
                        <p className="text-xs text-wt-text-muted" data-testid="carrefour-shopify-number">Shopify order: <span className="text-green-400">{order.shopify_order_name}</span></p>
                      )}
                      {order.carrefour.shopify_sync_error && (
                        <p className="text-xs text-amber-400" data-testid="carrefour-shopify-sync-error">Shopify sync failed: {order.carrefour.shopify_sync_error}</p>
                      )}
                      {order.carrefour.status_push_error && (
                        <p className="text-xs text-red-400" data-testid="carrefour-status-push-error">Carrefour push failed: {order.carrefour.status_push_error}</p>
                      )}

                      <CarrefourActions order={order} onRefresh={onRefresh} />
                    </div>
                  )}

                  {/* Order Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Payment Summary */}
                    <div className="bg-wt-paper rounded-lg border border-wt-border p-4">
                      <h3 className="text-sm font-medium text-wt-text-muted mb-3 flex items-center gap-2">
                        <CreditCard className="w-4 h-4" />
                        Payment Summary
                      </h3>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-wt-text-muted">Subtotal</span>
                          <span className="text-wt-text-primary">{formatCurrency(order.subtotal)}</span>
                        </div>
                        {order.discount_amount > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-wt-text-muted flex items-center gap-1">
                              <Percent className="w-3 h-3" />
                              Discount
                              {order.discount_codes?.length > 0 && (
                                <span className="text-xs text-wt-gold">({order.discount_codes.join(', ')})</span>
                              )}
                            </span>
                            <span className="text-red-400">-{formatCurrency(order.discount_amount)}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-sm">
                          <span className="text-wt-text-muted">Shipping</span>
                          <span className="text-wt-text-primary">{formatCurrency(order.shipping_cost)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-wt-text-muted">Tax{order.tax_inclusive ? ' (included)' : ''}</span>
                          <span className="text-wt-text-primary">{formatCurrency(order.tax ?? order.total_tax)}</span>
                        </div>
                        <Separator className="my-2 bg-wt-border" />
                        <div className="flex justify-between font-medium">
                          <span className="text-wt-text-primary">Total</span>
                          <span className="text-wt-gold text-lg">{formatCurrency(order.total_amount)}</span>
                        </div>
                        {order.total_refunded > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-red-400">Refunded</span>
                            <span className="text-red-400">-{formatCurrency(order.total_refunded)}</span>
                          </div>
                        )}
                        {/* Partial COD: Shopify total_outstanding tracking */}
                        {(parseFloat(order.amount_due) > 0 && order.payment_status !== 'paid') && (
                          <div className="pt-2 mt-1 border-t border-dashed border-wt-border space-y-1.5" data-testid="partial-cod-breakdown">
                            <div className="flex justify-between text-sm">
                              <span className="text-green-400">Paid (advance)</span>
                              <span className="text-green-400" data-testid="partial-paid-amount">{formatCurrency(order.paid_amount ?? (parseFloat(order.total_amount || 0) - parseFloat(order.amount_due || 0)))}</span>
                            </div>
                            <div className="flex justify-between text-sm font-semibold">
                              <span className="text-amber-400">Balance Due — collect on delivery</span>
                              <span className="text-amber-400" data-testid="partial-balance-due">{formatCurrency(order.amount_due)}</span>
                            </div>
                          </div>
                        )}
                        {/* Collection breakdown: link + cash + balance */}
                        {(() => {
                          const linkPaid = (order.payment_links || []).filter(l => l.is_paid)
                            .reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
                          let cashPaid = order.cash_collected ? (parseFloat(order.cash_amount) || 0) : 0;
                          if (order.reopen_cash_collected) cashPaid += parseFloat(order.reopen_cash_amount) || 0;
                          const total = parseFloat(order.total_amount) || 0;
                          const isPaid = order.payment_status === 'paid';
                          if (!linkPaid && !cashPaid && !isPaid && !['partially_paid', 'partial'].includes(order.payment_status)) return null;
                          const collected = linkPaid + cashPaid;
                          const balance = Math.max(0, total - collected);
                          return (
                            <div className="pt-2 mt-1 border-t border-dashed border-wt-border space-y-1.5" data-testid="payment-breakdown">
                              {linkPaid > 0 && (
                                <div className="flex justify-between text-sm">
                                  <span className="text-wt-text-muted">Paid via payment link</span>
                                  <span className="text-green-400" data-testid="breakdown-link-paid">{formatCurrency(linkPaid)}</span>
                                </div>
                              )}
                              {cashPaid > 0 && (
                                <div className="flex justify-between text-sm">
                                  <span className="text-wt-text-muted">Cash collected</span>
                                  <span className="text-green-400" data-testid="breakdown-cash-paid">{formatCurrency(cashPaid)}</span>
                                </div>
                              )}
                              {(linkPaid > 0 || cashPaid > 0) ? (
                                <div className="flex justify-between text-sm font-medium">
                                  <span className={balance > 0.5 && !isPaid ? 'text-amber-400' : 'text-wt-text-muted'}>
                                    {balance > 0.5 && !isPaid ? 'Balance due' : 'Fully collected'}
                                  </span>
                                  <span className={balance > 0.5 && !isPaid ? 'text-amber-400 font-semibold' : 'text-green-400'} data-testid="breakdown-balance">
                                    {balance > 0.5 && !isPaid ? formatCurrency(balance) : '✓'}
                                  </span>
                                </div>
                              ) : (isPaid && (
                                <div className="flex justify-between text-sm">
                                  <span className="text-wt-text-muted">Payment</span>
                                  <span className="text-green-400">Paid{order.paid_via ? ` · ${order.paid_via}` : ''}</span>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    </div>

                    {/* Shipping Rate Comparison */}
                    <ShippingRatesCompare order={order} />

                    {/* Fulfillment Info */}
                    <div className="bg-wt-paper rounded-lg border border-wt-border p-4">
                      <h3 className="text-sm font-medium text-wt-text-muted mb-3 flex items-center gap-2">
                        <Truck className="w-4 h-4" />
                        Fulfillment
                      </h3>
                      <div className="space-y-3">
                        {/* Split Shipments Display */}
                        {order.is_split && order.shipments && order.shipments.length > 0 ? (
                          <div className="space-y-3">
                            <div className="flex items-center gap-2 mb-2">
                              <GitBranch className="w-4 h-4 text-indigo-400" />
                              <span className="text-sm font-medium text-indigo-400">
                                Split into {order.shipments.length} Shipments
                              </span>
                            </div>
                            {order.shipments.map((shipment, idx) => (
                              <div key={shipment.shipment_id || idx} className="bg-wt-surface/50 rounded-lg p-3 border border-wt-border/50">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-sm font-medium text-wt-gold">
                                    {shipment.shipment_id}
                                  </span>
                                  <Badge className={`text-xs ${
                                    shipment.status === 'delivered' ? 'bg-green-500/20 text-green-400' :
                                    shipment.status === 'in_transit' ? 'bg-blue-500/20 text-blue-400' :
                                    shipment.status === 'booked' ? 'bg-cyan-500/20 text-cyan-400' :
                                    shipment.status === 'assigned' ? 'bg-purple-500/20 text-purple-400' :
                                    shipment.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                                    'bg-yellow-500/20 text-yellow-400'
                                  }`}>
                                    {shipment.status || 'pending'}
                                  </Badge>
                                </div>
                                <div className="text-xs text-wt-text-muted space-y-1">
                                  <div>
                                    {shipment.line_items?.map(item => 
                                      `${item.title} x${item.quantity}`
                                    ).join(', ')}
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <span>COD: {formatCurrency(shipment.cod_amount)}</span>
                                  </div>
                                  
                                  {/* Assignment Info */}
                                  {shipment.assigned_driver_name ? (
                                    <div className="flex items-center gap-2 text-wt-gold mt-2 pt-2 border-t border-wt-border/30">
                                      <User className="w-3 h-3" />
                                      <span>{shipment.assigned_driver_name}</span>
                                    </div>
                                  ) : shipment.awb_number ? (
                                    <div className="flex flex-col gap-2 mt-2 pt-2 border-t border-wt-border/30">
                                      {/* Courier Provider Info */}
                                      <div className="flex items-center gap-2">
                                        <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center">
                                          <Package className="w-3 h-3 text-blue-400" />
                                        </div>
                                        <span className="text-sm font-medium text-wt-text-primary capitalize">
                                          {shipment.courier_provider || 'Courier'}
                                        </span>
                                      </div>
                                      
                                      {/* AWB with copy */}
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs text-wt-text-muted">AWB</span>
                                        <div className="flex items-center gap-2">
                                          <span className="font-mono text-xs text-wt-gold">{shipment.awb_number}</span>
                                          <button
                                            onClick={() => {
                                              navigator.clipboard.writeText(shipment.awb_number);
                                              toast.success('AWB copied!');
                                            }}
                                            className="text-wt-text-muted hover:text-wt-gold"
                                          >
                                            <Copy className="w-3 h-3" />
                                          </button>
                                          {shipment.tracking_url && (
                                            <a 
                                              href={shipment.tracking_url} 
                                              target="_blank" 
                                              rel="noopener noreferrer"
                                              className="text-blue-400 hover:text-blue-300"
                                            >
                                              <ExternalLink className="w-3 h-3" />
                                            </a>
                                          )}
                                        </div>
                                      </div>
                                      
                                      {/* Weight & Dimensions for split shipment */}
                                      {(shipment.booked_weight || shipment.weight) && (
                                        <div className="flex items-center justify-between">
                                          <span className="text-xs text-wt-text-muted">Weight</span>
                                          <span className="text-xs text-wt-text-primary">{shipment.booked_weight || shipment.weight} kg</span>
                                        </div>
                                      )}
                                      {(shipment.booked_dimensions || shipment.dimensions) && (
                                        <div className="flex items-center justify-between">
                                          <span className="text-xs text-wt-text-muted">Dimensions</span>
                                          <span className="text-xs text-wt-text-primary">
                                            {typeof (shipment.booked_dimensions || shipment.dimensions) === 'object' 
                                              ? `${(shipment.booked_dimensions || shipment.dimensions)?.length}×${(shipment.booked_dimensions || shipment.dimensions)?.width}×${(shipment.booked_dimensions || shipment.dimensions)?.height} cm`
                                              : (shipment.booked_dimensions || shipment.dimensions)}
                                          </span>
                                        </div>
                                      )}
                                      
                                      {/* Sync, Track, Label Buttons */}
                                      <div className="flex gap-2 mt-1">
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="flex-1 h-7 text-xs"
                                          onClick={async () => {
                                            try {
                                              const token = localStorage.getItem('token');
                                              toast.info(`Syncing shipment ${shipment.shipment_number || idx + 1}...`);
                                              const res = await axios.post(
                                                `${API_BASE_URL}/couriers/shipments/sync`,
                                                { 
                                                  awb: shipment.awb_number,
                                                  courier_type: shipment.courier_provider,
                                                  order_id: order.id,
                                                  shipment_number: shipment.shipment_number
                                                },
                                                { headers: { Authorization: `Bearer ${token}` } }
                                              );
                                              toast.success(res.data?.message || 'Shipment synced');
                                              if (onRefresh) onRefresh();
                                            } catch (err) {
                                              console.error('Sync error:', err);
                                              toast.error(err.response?.data?.detail || 'Sync failed');
                                            }
                                          }}
                                        >
                                          <RefreshCw className="w-3 h-3 mr-1" />
                                          Sync
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="flex-1 h-7 text-xs"
                                          onClick={() => {
                                            const awb = shipment.awb_number;
                                            const courierType = (shipment.courier_provider || '').toLowerCase();
                                            let trackingUrl = shipment.tracking_url || '';
                                            if (!trackingUrl) {
                                              if (courierType === 'delhivery') {
                                                trackingUrl = `https://www.delhivery.com/track/package/${awb}`;
                                              } else if (courierType === 'bluedart') {
                                                trackingUrl = `https://www.bluedart.com/web/guest/trackdartresult?trackFor=awb&awb=${awb}`;
                                              } else if (courierType === 'dtdc') {
                                                trackingUrl = `https://tracking.dtdc.com/ctbs-tracking/customerInterface.tr?submitName=showCI498&cession=&cnNo=${awb}`;
                                              } else {
                                                trackingUrl = `https://www.delhivery.com/track/package/${awb}`;
                                              }
                                            }
                                            window.open(trackingUrl, '_blank');
                                          }}
                                        >
                                          <ExternalLink className="w-3 h-3 mr-1" />
                                          Track
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="default"
                                          className="flex-1 h-7 text-xs bg-wt-gold hover:bg-wt-gold/90 text-black"
                                          onClick={async () => {
                                            try {
                                              const token = localStorage.getItem('token');
                                              const awb = shipment.awb_number;
                                              
                                              if (!awb) {
                                                toast.error('No AWB number available');
                                                return;
                                              }
                                              
                                              toast.info('Downloading label...');
                                              
                                              // Find shipment ID from courier_shipments by AWB
                                              const res = await axios.get(
                                                `${API_BASE_URL}/couriers/shipments?awb=${awb}`,
                                                { headers: { Authorization: `Bearer ${token}` } }
                                              );
                                              const shipmentData = res.data.shipments?.[0];
                                              
                                              if (!shipmentData?.id) {
                                                // Shipment not in courier_shipments - try bulk endpoint
                                                try {
                                                  const labelRes = await axios.post(
                                                    `${API_BASE_URL}/couriers/shipments/bulk/opswarex-labels`,
                                                    { order_ids: [order.id], labels_per_page: 1 },
                                                    { 
                                                      headers: { Authorization: `Bearer ${token}` },
                                                      responseType: 'blob'
                                                    }
                                                  );
                                                  
                                                  const blob = new Blob([labelRes.data], { type: 'application/pdf' });
                                                  const url = window.URL.createObjectURL(blob);
                                                  const a = document.createElement('a');
                                                  a.href = url;
                                                  a.download = `shipping-label-${awb}.pdf`;
                                                  document.body.appendChild(a);
                                                  a.click();
                                                  window.URL.revokeObjectURL(url);
                                                  document.body.removeChild(a);
                                                  
                                                  toast.success('Label downloaded');
                                                  return;
                                                } catch (bulkErr) {
                                                  toast.error('Shipment not found');
                                                  return;
                                                }
                                              }
                                              
                                              // Download opswarex label
                                              const labelRes = await axios.get(
                                                `${API_BASE_URL}/couriers/shipments/${shipmentData.id}/opswarex-label`,
                                                { 
                                                  headers: { Authorization: `Bearer ${token}` },
                                                  responseType: 'blob'
                                                }
                                              );
                                              
                                              const blob = new Blob([labelRes.data], { type: 'application/pdf' });
                                              const url = window.URL.createObjectURL(blob);
                                              const a = document.createElement('a');
                                              a.href = url;
                                              a.download = `shipping-label-${awb}.pdf`;
                                              document.body.appendChild(a);
                                              a.click();
                                              window.URL.revokeObjectURL(url);
                                              document.body.removeChild(a);
                                              
                                              toast.success('Label downloaded');
                                            } catch (err) {
                                              console.error('Label error:', err);
                                              toast.error('Failed to download label');
                                            }
                                          }}
                                        >
                                          <Download className="w-3 h-3 mr-1" />
                                          Label
                                        </Button>
                                      </div>
                                      
                                      {/* Courier's Original Label link */}
                                      <button
                                        onClick={async () => {
                                          try {
                                            const token = localStorage.getItem('token');
                                            const awb = shipment.awb_number || shipment.awb;
                                            
                                            if (!awb) {
                                              toast.error('No AWB number available');
                                              return;
                                            }
                                            
                                            toast.info('Fetching courier\'s original label...');
                                            
                                            // Find shipment ID from courier_shipments by AWB
                                            const res = await axios.get(
                                              `${API_BASE_URL}/couriers/shipments?awb=${awb}`,
                                              { headers: { Authorization: `Bearer ${token}` } }
                                            );
                                            const shipmentData = res.data.shipments?.[0];
                                            
                                            if (!shipmentData?.id) {
                                              toast.error('Shipment not found in courier records');
                                              return;
                                            }
                                            
                                            // Check if label is available
                                            const labelCheckRes = await axios.get(
                                              `${API_BASE_URL}/v1/couriers/shipments/${shipmentData.id}/label`,
                                              { headers: { Authorization: `Bearer ${token}` } }
                                            );
                                            
                                            if (labelCheckRes.data?.download_url || labelCheckRes.data?.has_content) {
                                              // Download label as blob with auth token
                                              const labelRes = await axios.get(
                                                `${API_BASE_URL}/couriers/shipments/${shipmentData.id}/label/download`,
                                                { 
                                                  headers: { Authorization: `Bearer ${token}` },
                                                  responseType: 'blob'
                                                }
                                              );
                                              
                                              const blob = new Blob([labelRes.data], { type: 'application/pdf' });
                                              const url = window.URL.createObjectURL(blob);
                                              const a = document.createElement('a');
                                              a.href = url;
                                              a.download = `courier-label-${awb}.pdf`;
                                              document.body.appendChild(a);
                                              a.click();
                                              window.URL.revokeObjectURL(url);
                                              document.body.removeChild(a);
                                              
                                              toast.success('Courier label downloaded');
                                            } else if (labelCheckRes.data?.label_url) {
                                              // External URL - open in new tab
                                              window.open(labelCheckRes.data.label_url, '_blank');
                                              toast.info(labelCheckRes.data.message || 'Opening courier portal...');
                                            } else {
                                              toast.info('Courier original label not available');
                                            }
                                          } catch (err) {
                                            console.error('Courier label error:', err);
                                            toast.error('Failed to get courier label');
                                          }
                                        }}
                                        className="text-xs text-wt-text-muted hover:text-wt-gold flex items-center gap-1"
                                      >
                                        <FileText className="w-3 h-3" />
                                        Download Courier's Original Label
                                      </button>

                                      {/* Unassign Courier for this shipment (split order) */}
                                      {canBookCourier && !['delivered', 'in_transit', 'out_for_delivery'].includes((shipment.status || '').toLowerCase()) && (
                                        <button
                                          data-testid={`unassign-shipment-courier-${shipment.shipment_number || idx + 1}`}
                                          onClick={async () => {
                                            if (!window.confirm(`Unassign courier from Shipment ${shipment.shipment_number || idx + 1}?\n\nAWB ${shipment.awb_number} will be cancelled with ${shipment.courier_provider || 'the courier'}. Other shipments will not be affected.`)) return;
                                            try {
                                              const token = localStorage.getItem('token');
                                              const res = await axios.post(
                                                `${API_BASE_URL}/couriers/orders/${order.id}/shipments/${shipment.shipment_number || idx + 1}/unassign`,
                                                {},
                                                { headers: { Authorization: `Bearer ${token}` } }
                                              );
                                              toast.success(res.data?.message || 'Courier unassigned');
                                              if (onRefresh) onRefresh();
                                            } catch (err) {
                                              console.error('Unassign error:', err);
                                              toast.error(err.response?.data?.detail || 'Failed to unassign courier');
                                            }
                                          }}
                                          className="text-xs text-orange-400 hover:text-orange-300 flex items-center gap-1 mt-1"
                                        >
                                          <XCircle className="w-3 h-3" />
                                          Unassign Courier (Shipment {shipment.shipment_number || idx + 1})
                                        </button>
                                      )}
                                    </div>
                                  ) : (
                                    /* Assignment Buttons for pending shipments */
                                    <div className="flex gap-2 mt-2 pt-2 border-t border-wt-border/30">
                                      {(isAdmin || canAssignDriver) && (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="text-xs h-7 flex-1"
                                          onClick={() => {
                                            if (onAssignDriverToShipment) {
                                              onAssignDriverToShipment(order, shipment.shipment_number);
                                            } else if (onAssignDriver) {
                                              // Fallback to order-level assignment
                                              onAssignDriver(order);
                                            }
                                          }}
                                        >
                                          <User className="w-3 h-3 mr-1" />
                                          Driver
                                        </Button>
                                      )}
                                      {canBookCourier && (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="text-xs h-7 flex-1"
                                          onClick={() => {
                                            if (onBookCourierForShipment) {
                                              onBookCourierForShipment(order, shipment.shipment_number);
                                            } else if (onBookCourier) {
                                              // Fallback to order-level booking
                                              onBookCourier(order);
                                            }
                                          }}
                                        >
                                          <Package className="w-3 h-3 mr-1" />
                                          Courier
                                        </Button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (order.awb_number || order.courier_awb) ? (
                          /* Courier Fulfillment */
                          <div className="space-y-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                                <Package className="w-5 h-5 text-red-400" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-wt-text-primary capitalize">
                                  {order.courier_type || order.fulfillment_type || 'Courier'}
                                </p>
                                <p className="text-xs text-wt-text-muted">Courier Service</p>
                              </div>
                            </div>
                            <div className="bg-wt-surface/50 rounded-lg p-3 space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-wt-text-muted">AWB Number</span>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-mono text-wt-gold">{order.awb_number || order.courier_awb}</span>
                                  <button
                                    onClick={() => {
                                      navigator.clipboard.writeText(order.awb_number || order.courier_awb);
                                      toast.success('AWB copied!');
                                    }}
                                    className="text-wt-text-muted hover:text-wt-gold"
                                  >
                                    <Copy className="w-3 h-3" />
                                  </button>
                                </div>
                              </div>
                              {order.courier_status && (
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-wt-text-muted">Status</span>
                                  <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-xs capitalize">
                                    {order.courier_status.replace(/_/g, ' ')}
                                  </Badge>
                                </div>
                              )}
                              {/* Bluedart-specific: when an RTO leg is
                                  active the courier mints a brand-new
                                  waybill. Surface it so operators can
                                  track the return journey separately
                                  and reconcile the parcel when it
                                  lands. */}
                              {order.rto_awb_number && (
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-wt-text-muted">RTO AWB</span>
                                  <div className="flex items-center gap-2">
                                    <span
                                      className="text-sm font-mono text-rose-300"
                                      data-testid="rto-awb-number"
                                    >
                                      {order.rto_awb_number}
                                    </span>
                                    <button
                                      onClick={() => {
                                        navigator.clipboard.writeText(order.rto_awb_number);
                                        toast.success('RTO AWB copied!');
                                      }}
                                      className="text-wt-text-muted hover:text-rose-300"
                                    >
                                      <Copy className="w-3 h-3" />
                                    </button>
                                  </div>
                                </div>
                              )}
                              {/* Weight & Dimensions for billing proof */}
                              {(order.booked_weight || order.shipping_weight) && (
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-wt-text-muted">Booked Weight</span>
                                  <span className="text-xs text-wt-text-primary">{order.booked_weight || order.shipping_weight} kg</span>
                                </div>
                              )}
                              {(order.booked_dimensions || order.shipping_dimensions) && (
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-wt-text-muted">Booked Dims</span>
                                  <span className="text-xs text-wt-text-primary">
                                    {typeof (order.booked_dimensions || order.shipping_dimensions) === 'object' 
                                      ? `${order.booked_dimensions?.length || order.shipping_dimensions?.length}×${order.booked_dimensions?.width || order.shipping_dimensions?.width}×${order.booked_dimensions?.height || order.shipping_dimensions?.height} cm`
                                      : (order.booked_dimensions || order.shipping_dimensions)}
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="flex-1"
                                onClick={handleSyncTrackingStatus}
                                disabled={syncingTracking}
                              >
                                {syncingTracking ? (
                                  <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                                ) : (
                                  <RefreshCw className="w-3 h-3 mr-2" />
                                )}
                                Sync
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="flex-1"
                                onClick={() => {
                                  const awb = order.awb_number || order.courier_awb;
                                  const courierType = (order.courier_type || '').toLowerCase();
                                  let trackingUrl = '';
                                  if (courierType === 'delhivery') {
                                    trackingUrl = `https://www.delhivery.com/track/package/${awb}`;
                                  } else if (courierType === 'bluedart') {
                                    trackingUrl = `https://www.bluedart.com/web/guest/trackdartresult?trackFor=awb&awb=${awb}`;
                                  } else if (courierType === 'dtdc') {
                                    trackingUrl = `https://tracking.dtdc.com/ctbs-tracking/customerInterface.tr?submitName=showCI498&cession=&cnNo=${awb}`;
                                  } else {
                                    // Default to Delhivery for unknown couriers
                                    trackingUrl = `https://www.delhivery.com/track/package/${awb}`;
                                  }
                                  window.open(trackingUrl, '_blank');
                                }}
                              >
                                <ExternalLink className="w-3 h-3 mr-2" />
                                Track
                              </Button>
                              {/* Primary: Opswarex Branded Label */}
                              <Button
                                size="sm"
                                variant="default"
                                className="flex-1 bg-wt-gold hover:bg-wt-gold/90 text-black"
                                onClick={async () => {
                                  try {
                                    const token = localStorage.getItem('token');
                                    const awb = order.awb_number || order.courier_awb;
                                    
                                    if (!awb) {
                                      toast.error('No AWB number found for this order');
                                      return;
                                    }
                                    
                                    toast.info('Downloading shipping label...');

                                    // Try the shipment-id path first
                                    // (works for couriers that create
                                    // a courier_shipments row). If
                                    // empty, fall back to the
                                    // order-based label endpoint —
                                    // which covers Custom couriers
                                    // (AWB="SELF VEHICLE", hand-keyed
                                    // numbers, etc.) that bypass the
                                    // courier_shipments collection.
                                    const res = await axios.get(
                                      `${API_BASE_URL}/couriers/shipments?awb=${encodeURIComponent(awb)}`,
                                      { headers: { Authorization: `Bearer ${token}` } }
                                    );
                                    const shipmentData = res.data.shipments?.[0];

                                    let labelRes;
                                    if (shipmentData?.id) {
                                      labelRes = await axios.get(
                                        `${API_BASE_URL}/couriers/shipments/${shipmentData.id}/opswarex-label`,
                                        { 
                                          headers: { Authorization: `Bearer ${token}` },
                                          responseType: 'blob'
                                        }
                                      );
                                    } else {
                                      // Custom courier fallback: render
                                      // the label directly from the
                                      // order. No shipment row needed.
                                      labelRes = await axios.get(
                                        `${API_BASE_URL}/couriers/orders/${order.id}/opswarex-label`,
                                        { 
                                          headers: { Authorization: `Bearer ${token}` },
                                          responseType: 'blob'
                                        }
                                      );
                                    }

                                    // Create download link
                                    const blob = new Blob([labelRes.data], { type: 'application/pdf' });
                                    const url = window.URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    const safeAwb = String(awb).replace(/[^a-zA-Z0-9_-]/g, '_');
                                    a.download = `shipping-label-${safeAwb}.pdf`;
                                    document.body.appendChild(a);
                                    a.click();
                                    window.URL.revokeObjectURL(url);
                                    document.body.removeChild(a);
                                    
                                    toast.success('Label downloaded successfully');
                                  } catch (err) {
                                    console.error('Label download error:', err);
                                    const errorMsg = err.response?.data?.detail || err.message || 'Failed to download label';
                                    toast.error(errorMsg);
                                  }
                                }}
                              >
                                <Download className="w-3 h-3 mr-2" />
                                Label
                              </Button>
                            </div>
                            {/* Secondary: Courier's Original Label */}
                            <div className="mt-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="w-full text-xs text-wt-text-muted hover:text-wt-text-primary"
                                onClick={async () => {
                                  try {
                                    const token = localStorage.getItem('token');
                                    const awb = order.awb_number || order.courier_awb;
                                    toast.info('Downloading courier label...');
                                    
                                    // First get shipment ID
                                    const res = await axios.get(
                                      `${API_BASE_URL}/couriers/shipments?awb=${awb}`,
                                      { headers: { Authorization: `Bearer ${token}` } }
                                    );
                                    const shipmentData = res.data.shipments?.[0];
                                    if (!shipmentData?.id) {
                                      toast.error('Shipment not found');
                                      return;
                                    }
                                    
                                    // Check if label is available
                                    const labelCheckRes = await axios.get(
                                      `${API_BASE_URL}/v1/couriers/shipments/${shipmentData.id}/label`,
                                      { headers: { Authorization: `Bearer ${token}` } }
                                    );
                                    
                                    if (labelCheckRes.data?.download_url || labelCheckRes.data?.has_content) {
                                      // Download label as blob with auth token
                                      const labelRes = await axios.get(
                                        `${API_BASE_URL}/couriers/shipments/${shipmentData.id}/label/download`,
                                        { 
                                          headers: { Authorization: `Bearer ${token}` },
                                          responseType: 'blob'
                                        }
                                      );
                                      
                                      // Create download link
                                      const blob = new Blob([labelRes.data], { type: 'application/pdf' });
                                      const url = window.URL.createObjectURL(blob);
                                      const a = document.createElement('a');
                                      a.href = url;
                                      a.download = `courier-label-${awb}.pdf`;
                                      document.body.appendChild(a);
                                      a.click();
                                      window.URL.revokeObjectURL(url);
                                      document.body.removeChild(a);
                                      
                                      toast.success('Courier label downloaded successfully');
                                    } else if (labelCheckRes.data?.label_url) {
                                      // External URL - open in new tab (external URLs don't need our auth)
                                      window.open(labelCheckRes.data.label_url, '_blank');
                                      toast.success('Opening courier label...');
                                    } else {
                                      toast.error(labelCheckRes.data?.error || 'Courier label not available');
                                    }
                                  } catch (err) {
                                    console.error('Courier label error:', err);
                                    toast.error('Failed to get courier label');
                                  }
                                }}
                              >
                                <FileText className="w-3 h-3 mr-1" />
                                Download Courier's Original Label
                              </Button>
                            </div>
                          </div>
                        ) : order.assigned_driver_name ? (
                          /* Driver Fulfillment */
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-wt-gold/20 flex items-center justify-center">
                              <User className="w-5 h-5 text-wt-gold" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-wt-text-primary">{order.assigned_driver_name}</p>
                              <p className="text-xs text-wt-text-muted">Assigned Driver</p>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-wt-text-muted">No fulfillment assigned</p>
                        )}
                        
                        {order.assigned_at && (
                          <div className="text-xs text-wt-text-muted">
                            Assigned: {formatDate(order.assigned_at)}
                          </div>
                        )}
                        {order.delivered_at && (
                          <div className="text-xs text-green-400">
                            Delivered: {formatDate(order.delivered_at)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Not synced to Shopify (manual orders) */}
                  {!order.shopify_order_id && (order.created_from === 'manual' || order.source === 'manual' || order.created_by_name) && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3" data-testid="not-synced-banner">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-red-400">Not synced to Shopify</p>
                          {order.shopify_push_error && (
                            <p className="text-xs text-wt-text-muted mt-0.5 break-words">Last error: {order.shopify_push_error}</p>
                          )}
                        </div>
                        <Button
                          size="sm"
                          disabled={pushingShopify}
                          onClick={async () => {
                            setPushingShopify(true);
                            try {
                              const token = localStorage.getItem('token');
                              const res = await axios.post(`${API_BASE_URL}/orders/${order.id}/push-to-shopify`, {}, { headers: { Authorization: `Bearer ${token}` } });
                              toast.success(`Pushed to Shopify — order ${res.data.order_number || ''}`);
                              if (onRefresh) {
                                const r2 = await axios.get(`${API_BASE_URL}/orders/${order.id}`, { headers: { Authorization: `Bearer ${token}` } });
                                onRefresh(r2.data);
                              }
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Shopify push failed');
                              if (onRefresh) onRefresh();
                            } finally {
                              setPushingShopify(false);
                            }
                          }}
                          className="bg-red-500/20 text-red-300 hover:bg-red-500/30 border border-red-500/30 shrink-0"
                          data-testid="push-to-shopify-btn"
                        >
                          {pushingShopify ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Push to Shopify'}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Home Centre: delivery must be confirmed in their portal (no API) */}
                  {order.source === 'homecentre' && (order.homecentre || {}).portal_delivery_confirmation_required && (
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3" data-testid="homecentre-portal-confirm-banner">
                      <p className="text-sm font-semibold text-amber-400">Confirm delivery on Home Centre</p>
                      <p className="text-xs text-wt-text-muted mt-1">Delivered in the app{(order.homecentre || {}).delivered_by_driver ? ` by ${order.homecentre.delivered_by_driver}` : ''}. Home Centre has no API to mark orders delivered — please confirm this delivery in the Home Centre seller portal.</p>
                    </div>
                  )}

                  {/* Partial delivery + status override note */}
                  {order.partial_delivery && (
                    <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3" data-testid="partial-delivery-banner">
                      <p className="text-sm font-semibold text-orange-400">Partially Delivered</p>
                      <p className="text-xs text-wt-text-muted mt-1">
                        Customer rejected {(order.rejected_items || []).reduce((s, i) => s + (i.quantity_returning || 1), 0)} item(s) at delivery
                        {order.return_number ? ` — return ${order.return_number} created (items with driver)` : ''}.
                      </p>
                    </div>
                  )}
                  {order.delivery_notes && (
                    <div className="bg-sky-500/10 border border-sky-500/30 rounded-lg p-3" data-testid="status-note-banner">
                      <p className="text-xs font-semibold text-sky-400">Status update note</p>
                      <p className="text-sm text-wt-text-primary mt-0.5">{order.delivery_notes}</p>
                    </div>
                  )}

                  {/* Driver note (shown to driver on My Deliveries) */}
                  <div className="bg-wt-paper rounded-lg border border-wt-border p-4" data-testid="driver-note-card">
                    <h3 className="text-sm font-medium text-wt-text-muted mb-2">📝 Delivery Note for Driver</h3>
                    <div className="flex gap-2">
                      <Input
                        value={driverNoteInput}
                        onChange={(e) => setDriverNoteInput(e.target.value)}
                        placeholder="e.g., Call before arriving, gate code 1234"
                        className="h-8 bg-wt-bg border-wt-border text-wt-text-primary text-sm"
                        data-testid="driver-note-input"
                      />
                      <Button
                        size="sm"
                        onClick={saveDriverNote}
                        disabled={savingDriverNote || (driverNoteInput || '') === (order.driver_note || '')}
                        data-testid="save-driver-note-btn"
                      >
                        {savingDriverNote ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                      </Button>
                    </div>
                    {order.driver_note_by && (
                      <p className="text-[11px] text-wt-text-muted mt-1.5">Last set by {order.driver_note_by}</p>
                    )}
                  </div>

                  {/* Customer Location Pin - Visible to All Users */}
                  {order.delivery_location && order.delivery_location.lat && order.delivery_location.lng ? (
                    <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-lg border border-green-500/30 p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-medium text-green-400 flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          Customer Location Received
                        </h3>
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                          {order.delivery_location.source === 'customer_pin'
                            ? 'From Tracking Page'
                            : order.delivery_location.source === 'manual_entry'
                              ? 'Manual Pin'
                              : 'WhatsApp'}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const googleMapsUrl = `https://www.google.com/maps?q=${order.delivery_location.lat},${order.delivery_location.lng}`;
                            copyToClipboard(googleMapsUrl);
                            toast.success('Location URL copied to clipboard!');
                          }}
                          className="border-green-500/30 text-green-400 hover:bg-green-500/20"
                          data-testid="copy-location-url-details"
                        >
                          <Copy className="w-3 h-3 mr-1" />
                          Copy URL
                        </Button>
                        <a
                          href={`https://www.google.com/maps?q=${order.delivery_location.lat},${order.delivery_location.lng}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-md border border-blue-500/30 text-blue-400 hover:bg-blue-500/20"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Open Maps
                        </a>
                        <a
                          href={`https://www.google.com/maps/dir/?api=1&destination=${order.delivery_location.lat},${order.delivery_location.lng}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-md bg-green-500/20 text-green-400 hover:bg-green-500/30"
                        >
                          <Navigation className="w-3 h-3" />
                          Navigate
                        </a>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setShowPinInput(!showPinInput)}
                          className="text-wt-text-muted hover:text-wt-text-primary"
                          data-testid="update-location-pin-btn"
                        >
                          <MapPin className="w-3 h-3 mr-1" />
                          Update Pin
                        </Button>
                      </div>
                      {showPinInput && (
                        <div className="flex gap-2 mt-3">
                          <Input
                            value={pinInput}
                            onChange={(e) => setPinInput(e.target.value)}
                            placeholder="25.110054, 55.224058"
                            className="h-8 bg-wt-bg border-wt-border text-wt-text-primary text-sm"
                            data-testid="location-pin-input"
                          />
                          <Button
                            size="sm"
                            onClick={saveLocationPin}
                            disabled={savingPin}
                            className="bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/30"
                            data-testid="save-location-pin-btn"
                          >
                            {savingPin ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-wt-paper rounded-lg border border-wt-border p-4" data-testid="add-location-pin-card">
                      <h3 className="text-sm font-medium text-wt-text-muted mb-2 flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        Add Location Pin Manually
                      </h3>
                      <p className="text-xs text-wt-text-muted mb-2">
                        Paste coordinates from Google Maps, e.g. 25.110054, 55.224058
                      </p>
                      <div className="flex gap-2">
                        <Input
                          value={pinInput}
                          onChange={(e) => setPinInput(e.target.value)}
                          placeholder="25.110054, 55.224058"
                          className="h-8 bg-wt-bg border-wt-border text-wt-text-primary text-sm"
                          data-testid="location-pin-input"
                        />
                        <Button
                          size="sm"
                          onClick={saveLocationPin}
                          disabled={savingPin || !pinInput.trim()}
                          className="bg-wt-gold hover:bg-wt-gold-hover text-black"
                          data-testid="save-location-pin-btn"
                        >
                          {savingPin ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save Pin'}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Proof of Delivery Section */}
                  {(order.proof_of_delivery_urls?.length > 0 || order.proof_of_delivery_url) && (
                    <div className="bg-wt-paper rounded-lg border border-wt-border p-4">
                      <h3 className="text-sm font-medium text-wt-text-muted mb-3 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-400" />
                        Proof of Delivery
                        {order.proof_of_delivery_urls?.length > 1 && (
                          <span className="text-xs text-wt-text-muted">({order.proof_of_delivery_urls.length} photos)</span>
                        )}
                      </h3>
                      <div className={`grid gap-3 ${(order.proof_of_delivery_urls?.length || 1) > 1 ? 'grid-cols-2 sm:grid-cols-3' : ''}`} data-testid="pod-display-grid">
                        {(order.proof_of_delivery_urls?.length > 0 ? order.proof_of_delivery_urls : [order.proof_of_delivery_url]).map((podUrl, podIdx) => (
                          <ImageLightbox 
                            key={podIdx}
                            src={podUrl.startsWith('http') 
                              ? podUrl 
                              : `${process.env.REACT_APP_BACKEND_URL}${podUrl}`
                            }
                            alt={`Proof of Delivery ${podIdx + 1}`}
                            className="w-full max-w-md rounded-lg border border-wt-border cursor-pointer hover:opacity-90 transition-opacity"
                            thumbnailClassName="w-full h-48 object-cover rounded-lg"
                          />
                        ))}
                      </div>
                      <div className="relative">
                        <p className="text-xs text-wt-text-muted mt-2">
                          Click image to view full size
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Driver-captured delivery location ───────────────────── */}
                  {order.driver_delivery_location && order.driver_delivery_location.lat && order.driver_delivery_location.lng && (
                    <div className="bg-wt-paper rounded-lg border border-green-500/30 p-4" data-testid="admin-delivery-location-block">
                      <h3 className="text-sm font-medium text-green-400 mb-3 flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        Delivered At (Driver GPS)
                        {order.driver_delivery_location.accuracy_m && (
                          <Badge className="ml-auto bg-green-500/15 text-green-300 border-green-500/30 text-[10px]">
                            ±{Math.round(order.driver_delivery_location.accuracy_m)}m
                          </Badge>
                        )}
                      </h3>
                      {order.driver_delivery_location.address && (
                        <p className="text-sm text-wt-text-secondary mb-2">
                          {order.driver_delivery_location.address}
                        </p>
                      )}
                      <p className="text-xs font-mono text-wt-text-muted mb-3">
                        {Number(order.driver_delivery_location.lat).toFixed(6)}, {Number(order.driver_delivery_location.lng).toFixed(6)}
                      </p>
                      {order.driver_delivery_location.captured_at && (
                        <p className="text-xs text-wt-text-muted mb-3">
                          Stamped {formatDate(order.driver_delivery_location.captured_at)}
                          {order.driver_delivery_location.captured_by_user_name && (
                            <> by <span className="text-wt-text-secondary">{order.driver_delivery_location.captured_by_user_name}</span></>
                          )}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-2">
                        <a
                          href={`https://www.google.com/maps?q=${order.driver_delivery_location.lat},${order.driver_delivery_location.lng}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-md border border-blue-500/30 text-blue-400 hover:bg-blue-500/20"
                          data-testid="admin-delivery-map-link"
                        >
                          <ExternalLink className="w-3 h-3" /> View on Google Maps
                        </a>
                        <a
                          href={`https://www.google.com/maps/dir/?api=1&destination=${order.driver_delivery_location.lat},${order.driver_delivery_location.lng}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-md bg-green-500/20 text-green-400 hover:bg-green-500/30"
                        >
                          <Navigation className="w-3 h-3" /> Directions
                        </a>
                        <button
                          type="button"
                          onClick={() => {
                            const url = `https://www.google.com/maps?q=${order.driver_delivery_location.lat},${order.driver_delivery_location.lng}`;
                            copyToClipboard(url);
                            toast.success('Delivery location URL copied');
                          }}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-md border border-wt-border text-wt-text-secondary hover:bg-wt-subtle"
                        >
                          <Copy className="w-3 h-3" /> Copy URL
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Tags */}
                  {order.tags && order.tags.length > 0 && (
                    <div className="bg-wt-paper rounded-lg border border-wt-border p-4">
                      <h3 className="text-sm font-medium text-wt-text-muted mb-3 flex items-center gap-2">
                        <Tag className="w-4 h-4" />
                        Tags
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {order.tags.map((tag, idx) => (
                          <Badge key={idx} variant="outline" className="border-wt-border text-wt-text-secondary">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  {order.notes && (
                    <div className="bg-wt-paper rounded-lg border border-wt-border p-4">
                      <h3 className="text-sm font-medium text-wt-text-muted mb-3 flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Notes
                      </h3>
                      <p className="text-sm text-wt-text-secondary whitespace-pre-wrap">{order.notes}</p>
                    </div>
                  )}

                  {/* Risk Assessment */}
                  {order.risk_level && (
                    <div className={`bg-wt-paper rounded-lg border p-4 ${
                      order.risk_level === 'accept' ? 'border-green-500/30' :
                      order.risk_level === 'investigate' ? 'border-yellow-500/30' :
                      'border-red-500/30'
                    }`}>
                      <h3 className="text-sm font-medium text-wt-text-muted mb-2 flex items-center gap-2">
                        <Shield className="w-4 h-4" />
                        Fraud Risk
                      </h3>
                      <Badge className={
                        order.risk_level === 'accept' ? 'bg-green-500/20 text-green-400' :
                        order.risk_level === 'investigate' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-red-500/20 text-red-400'
                      }>
                        {order.risk_level.charAt(0).toUpperCase() + order.risk_level.slice(1)}
                      </Badge>
                    </div>
                  )}

                  {/* Return/Replacement Info */}
                  {order.return_replacement_type && (
                    <div className="bg-wt-paper rounded-lg border border-blue-500/30 p-4">
                      <h3 className="text-sm font-medium text-blue-400 mb-3 flex items-center gap-2">
                        <RefreshCw className="w-4 h-4" />
                        Return / Replacement
                      </h3>
                      <div className="space-y-2 text-sm">
                        <p><span className="text-wt-text-muted">Type:</span> <span className="text-wt-text-primary capitalize">{order.return_replacement_type.replace(/_/g, ' ')}</span></p>
                        <p><span className="text-wt-text-muted">Reason:</span> <span className="text-wt-text-primary capitalize">{order.return_replacement_reason?.replace(/_/g, ' ')}</span></p>
                        {order.return_replacement_notes && (
                          <p><span className="text-wt-text-muted">Notes:</span> <span className="text-wt-text-secondary">{order.return_replacement_notes}</span></p>
                        )}
                        {order.refund_status && (
                          <p><span className="text-wt-text-muted">Refund Status:</span> 
                            <Badge className={`ml-2 ${order.refund_status === 'issued' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                              {order.refund_status === 'issued' ? 'Issued' : 'Not Issued'}
                            </Badge>
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Action Panel - for all users with update permission */}
                  <DriverActionPanel 
                    order={order}
                    onOrderUpdate={onRefresh}
                  />
                </>
              )}

              {activeTab === 'items' && (
                <div className="space-y-3">
                  {order.items?.map((item, idx) => (
                    <div key={idx} className="bg-wt-paper rounded-lg border border-wt-border p-4 flex gap-4">
                      {item.image_url ? (
                        <ImageLightbox 
                          src={item.image_url} 
                          alt={item.name}
                          className="w-20 h-20"
                          thumbnailClassName="w-20 h-20 object-cover rounded-lg border border-wt-border"
                        />
                      ) : (
                        <div className="w-20 h-20 bg-wt-bg rounded-lg border border-wt-border flex items-center justify-center">
                          <Package className="w-8 h-8 text-wt-text-muted" />
                        </div>
                      )}
                      <div className="flex-1">
                        <h4 className="font-medium text-wt-text-primary">{item.name || item.title}</h4>
                        {item.variant_title && (
                          <p className="text-sm text-wt-text-muted">{item.variant_title}</p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-sm">
                          {item.sku && (
                            <span className="text-wt-text-muted">SKU: {item.sku}</span>
                          )}
                          <span className="text-wt-text-secondary">Qty: {item.quantity}</span>
                          <span className="text-wt-gold font-medium">{formatCurrency(item.price)}</span>
                        </div>
                        {(item.origin_location_name || item.origin_location_id) && (
                          <div
                            className="mt-1.5 inline-flex items-center gap-1 text-xs text-blue-300"
                            data-testid={`order-item-ships-from-${item.sku || idx}`}
                            title={
                              item.origin_location_name
                                ? `Shopify origin location: ${item.origin_location_name}`
                                : `Shopify origin location id: ${item.origin_location_id}`
                            }
                          >
                            <MapPin className="w-3 h-3" />
                            <span>
                              Ships from: {item.origin_location_name || `Location ${item.origin_location_id}`}
                            </span>
                          </div>
                        )}
                        {item.marketplace_vendor_name && (
                          <Badge
                            className="mt-2 bg-violet-500/15 text-violet-300 border border-violet-500/30"
                            data-testid={`item-mp-vendor-${item.sku || idx}`}
                            title={`Fulfilled by marketplace vendor: ${item.marketplace_vendor_name}`}
                          >
                            <Store className="w-3 h-3 mr-1" />
                            {item.marketplace_vendor_name}
                          </Badge>
                        )}
                        {item.is_replacement && (
                          <Badge className="mt-2 bg-blue-500/20 text-blue-400 border-blue-500/30">
                            Replacement (Original: {item.original_sku})
                          </Badge>
                        )}
                        {item.fulfillment_status && (
                          <Badge className={`mt-2 ${
                            item.fulfillment_status === 'fulfilled' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {item.fulfillment_status || 'Unfulfilled'}
                          </Badge>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-wt-text-primary">{formatCurrency(item.price * item.quantity)}</p>
                        {item.total_discount > 0 && (
                          <p className="text-sm text-red-400">-{formatCurrency(item.total_discount)}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'customer' && (
                <CustomerTab 
                  order={order} 
                  copyToClipboard={copyToClipboard}
                  onRefresh={onRefresh}
                  isAdmin={isAdmin}
                />
              )}

              {activeTab === 'timeline' && (
                <div className="space-y-4">
                  {/* Order Timeline */}
                  <div className="bg-wt-paper rounded-lg border border-wt-border p-4">
                    <h3 className="text-sm font-medium text-wt-text-muted mb-4">Order Timeline</h3>
                    <div className="space-y-4">
                      {order.created_at && (
                        <TimelineItem 
                          icon={<Package className="w-4 h-4" />}
                          title="Order Created"
                          subtitle={order.created_by_name ? `by ${order.created_by_name}${order.created_from ? ` (${String(order.created_from).replace(/_/g, ' ')})` : ''}` : undefined}
                          time={formatDate(order.created_at_shopify || order.created_at)}
                          color="text-blue-400"
                        />
                      )}
                      {order.packed_at && (
                        <TimelineItem 
                          icon={<Package className="w-4 h-4" />}
                          title="Order Packed"
                          subtitle={byLine('packed', 'packed_by_name')}
                          time={formatDate(order.packed_at)}
                          color="text-amber-400"
                        />
                      )}
                      {order.production_started_at && (
                        <TimelineItem 
                          icon={<Hammer className="w-4 h-4" />}
                          title="Production Started"
                          subtitle={[
                            byLine('in_production', 'production_started_by_name'),
                            order.production_estimated_date ? `Expected ready: ${order.production_estimated_date}` : null,
                            order.production_notes || null,
                          ].filter(Boolean).join(' · ') || undefined}
                          time={formatDate(order.production_started_at)}
                          color="text-amber-400"
                        />
                      )}
                      {order.production_completed_at && (
                        <TimelineItem 
                          icon={<CheckCircle className="w-4 h-4" />}
                          title="Production Completed"
                          subtitle={byLine('production_completed', 'production_completed_by_name')}
                          time={formatDate(order.production_completed_at)}
                          color="text-emerald-400"
                        />
                      )}
                      {order.ready_for_dispatch_at && (
                        <TimelineItem 
                          icon={<Package className="w-4 h-4" />}
                          title="Ready For Dispatch"
                          subtitle={byLine('ready_for_dispatch', 'ready_for_dispatch_by_name')}
                          time={formatDate(order.ready_for_dispatch_at)}
                          color="text-cyan-400"
                        />
                      )}
                      
                      {/* Courier Booked - check shipments or order level */}
                      {(order.courier_booked_at || order.shipments?.some(s => s.booked_at)) && (
                        <TimelineItem 
                          icon={<Truck className="w-4 h-4" />}
                          title={`Courier Booked${order.courier_type ? ` (${order.courier_type})` : order.shipments?.[0]?.courier_provider ? ` (${order.shipments[0].courier_provider})` : ''}`}
                          subtitle={order.awb_number || order.courier_awb || order.shipments?.[0]?.awb_number ? `AWB: ${order.awb_number || order.courier_awb || order.shipments?.[0]?.awb_number}` : undefined}
                          time={formatDate(order.courier_booked_at || order.shipments?.find(s => s.booked_at)?.booked_at)}
                          color="text-indigo-400"
                        />
                      )}
                      
                      {order.assigned_at && (
                        <TimelineItem 
                          icon={<User className="w-4 h-4" />}
                          title={`Assigned to ${order.assigned_driver_name}`}
                          subtitle={byLine('assigned', 'assigned_by_name')}
                          time={formatDate(order.assigned_at)}
                          color="text-purple-400"
                        />
                      )}
                      {order.accepted_at && (
                        <TimelineItem 
                          icon={<CheckCircle className="w-4 h-4" />}
                          title="Accepted by Driver"
                          subtitle={byLine('accepted', 'accepted_by_name') || (order.assigned_driver_name ? `by ${order.assigned_driver_name}` : undefined)}
                          time={formatDate(order.accepted_at)}
                          color="text-blue-400"
                        />
                      )}
                      {order.picked_at && (
                        <TimelineItem 
                          icon={<Package className="w-4 h-4" />}
                          title="Picked Up"
                          subtitle={byLine('picked', 'picked_by_name')}
                          time={formatDate(order.picked_at)}
                          color="text-purple-400"
                        />
                      )}
                      
                      {/* Courier In Transit */}
                      {(order.in_transit_at || order.courier_status === 'in_transit' || order.shipments?.some(s => s.status === 'in_transit')) && (
                        <TimelineItem 
                          icon={<Truck className="w-4 h-4" />}
                          title="In Transit"
                          time={order.in_transit_at ? formatDate(order.in_transit_at) : 'Status updated'}
                          color="text-blue-400"
                        />
                      )}
                      
                      {order.on_the_way_at && (
                        <TimelineItem 
                          icon={<Truck className="w-4 h-4" />}
                          title="On The Way"
                          subtitle={byLine('on_the_way', 'on_the_way_by_name')}
                          time={formatDate(order.on_the_way_at)}
                          color="text-orange-400"
                        />
                      )}
                      
                      {/* Courier Out for Delivery */}
                      {(order.out_for_delivery_at || order.courier_status === 'out_for_delivery' || order.shipments?.some(s => s.status === 'out_for_delivery')) && (
                        <TimelineItem 
                          icon={<Truck className="w-4 h-4" />}
                          title="Out for Delivery"
                          time={order.out_for_delivery_at ? formatDate(order.out_for_delivery_at) : 'Status updated'}
                          color="text-orange-400"
                        />
                      )}
                      
                      {/* Delivered — renders from `delivered_at` (driver
                          or webhook), `courier_status === 'delivered'` (poll),
                          or shipment-level delivered status (split orders).
                          Mirrors the In Transit / Out for Delivery guards. */}
                      {(order.delivered_at || order.courier_status === 'delivered' || order.fulfillment_status === 'delivered' || order.status === 'delivered' || order.shipments?.some(s => s.status === 'delivered' || s.courier_status === 'delivered')) && (
                        <TimelineItem
                          icon={<CheckCircle className="w-4 h-4" />}
                          title="Delivered"
                          subtitle={[byLine('delivered', 'delivered_by_name'), (order.courier_awb || order.awb_number) ? `AWB: ${order.courier_awb || order.awb_number}` : null].filter(Boolean).join(' · ') || undefined}
                          time={order.delivered_at ? formatDate(order.delivered_at) : 'Status updated'}
                          color="text-green-400"
                        />
                      )}

                      {/* RTO Initiated — courier flagged the package
                          for return. Backend stamps `rto_initiated_at`
                          on the first sync into this state; until that
                          stamp lands, fall back to `courier_status` /
                          shipment-level RTO substates. */}
                      {(order.rto_initiated_at || order.courier_status === 'rto_initiated' || order.shipments?.some(s => s.status === 'rto_initiated' || s.courier_status === 'rto_initiated')) && (
                        <TimelineItem
                          icon={<RotateCcw className="w-4 h-4" />}
                          title="RTO Initiated"
                          subtitle={order.courier_awb || order.awb_number ? `AWB: ${order.courier_awb || order.awb_number}` : undefined}
                          time={order.rto_initiated_at ? formatDate(order.rto_initiated_at) : 'Status updated'}
                          color="text-rose-300"
                        />
                      )}

                      {/* RTO In Transit — package is on the return leg
                          back to the warehouse. */}
                      {(order.rto_in_transit_at || order.courier_status === 'rto_in_transit' || order.shipments?.some(s => s.status === 'rto_in_transit' || s.courier_status === 'rto_in_transit')) && (
                        <TimelineItem
                          icon={<Truck className="w-4 h-4" />}
                          title="RTO In Transit"
                          subtitle={order.courier_awb || order.awb_number ? `AWB: ${order.courier_awb || order.awb_number}` : undefined}
                          time={order.rto_in_transit_at ? formatDate(order.rto_in_transit_at) : 'Status updated'}
                          color="text-rose-400"
                        />
                      )}

                      {/* RTO Delivered — package is physically back
                          at origin. Backend auto-creates a return doc
                          (status=delivered → QC Pending tab) on this
                          transition; merchant inwards from there. */}
                      {(order.rto_delivered_at || order.courier_status === 'rto_delivered' || order.shipments?.some(s => s.status === 'rto_delivered' || s.courier_status === 'rto_delivered')) && (
                        <TimelineItem
                          icon={<CheckCircle className="w-4 h-4" />}
                          title="RTO Delivered"
                          subtitle={order.courier_awb || order.awb_number ? `AWB: ${order.courier_awb || order.awb_number}` : undefined}
                          time={order.rto_delivered_at ? formatDate(order.rto_delivered_at) : 'Status updated'}
                          color="text-rose-500"
                        />
                      )}
                      
                      {/* Cash Collected - Only for Driver flow with COD orders (not courier) */}
                      {order.cash_collected_at && !order.courier_booked_at && !order.courier_awb && !order.awb_number && (
                        <TimelineItem 
                          icon={<DollarSign className="w-4 h-4" />}
                          title="Cash Collected"
                          subtitle={order.cash_collected_amount ? `Amount: ${formatCurrency(order.cash_collected_amount)}` : undefined}
                          time={formatDate(order.cash_collected_at)}
                          color="text-green-400"
                        />
                      )}
                      
                      {order.failed_at && (
                        <TimelineItem 
                          icon={<XCircle className="w-4 h-4" />}
                          title={`Failed: ${order.failure_reason || 'Unknown'}`}
                          time={formatDate(order.failed_at)}
                          color="text-red-400"
                        />
                      )}
                      
                      {/* Cancelled */}
                      {order.cancelled_at && (
                        <TimelineItem 
                          icon={<XCircle className="w-4 h-4" />}
                          title="Order Cancelled"
                          subtitle={order.cancellation_reason}
                          time={formatDate(order.cancelled_at)}
                          color="text-red-400"
                        />
                      )}
                      
                      {order.reopened_at && (
                        <TimelineItem 
                          icon={<RotateCcw className="w-4 h-4" />}
                          title={`Reopened by ${order.reopened_by_name}`}
                          subtitle={order.reopen_reason}
                          time={formatDate(order.reopened_at)}
                          color="text-amber-400"
                        />
                      )}
                      {order.return_replacement_at && (
                        <TimelineItem 
                          icon={<RefreshCw className="w-4 h-4" />}
                          title={`${order.return_replacement_type?.replace(/_/g, ' ')} initiated`}
                          subtitle={order.return_replacement_reason?.replace(/_/g, ' ')}
                          time={formatDate(order.return_replacement_at)}
                          color="text-blue-400"
                        />
                      )}
                      {order.refund_date && order.refund_status === 'issued' && (
                        <TimelineItem 
                          icon={<DollarSign className="w-4 h-4" />}
                          title={`Refund Issued: ${formatCurrency(order.refund_amount)}`}
                          time={formatDate(order.refund_date)}
                          color="text-green-400"
                        />
                      )}
                      
                      {/* MamoPay Payment */}
                      {order.mamopay_paid && order.mamopay_paid_at && (
                        <TimelineItem 
                          icon={<CreditCard className="w-4 h-4" />}
                          title="Payment Received via MamoPay"
                          subtitle={order.timeline?.find(t => t.type === 'payment_received')?.description || `Collected by ${order.assigned_driver_name || 'Driver'}`}
                          time={formatDate(order.mamopay_paid_at)}
                          color="text-green-400"
                        />
                      )}
                      
                      {/* Dynamic Timeline Entries (from order.timeline array) */}
                      {(() => {
                        // `courier_status_update` rows are already covered
                        // by the hard-coded timeline items above (Courier
                        // Booked, In Transit, Out for Delivery, Delivered),
                        // which draw from order.courier_status / shipment
                        // statuses. Rendering them again from the dynamic
                        // array would double-print every courier step.
                        // This change is intentional & scoped to the Order
                        // Timeline UI; the raw `timeline[]` in the DB is
                        // left untouched so analytics / audit-log endpoints
                        // still see the full history.
                        const entries = (order.timeline || []).filter(t => {
                          if (t.type === 'payment_received' && order.mamopay_paid) return false;
                          if (t.type === 'courier_status_update') return false;
                          return true;
                        });

                        return entries.map((entry, idx) => {
                          // Legacy courier_status_update rows from
                          // before the schema fix stored the text in
                          // `message` (not title/description) and
                          // rendered as a blank clock icon. Derive
                          // fallbacks so they render correctly.
                          const fallbackTitle = entry.title
                            || (entry.status && `${entry.status.replace(/_/g, ' ')
                                .replace(/\b\w/g, c => c.toUpperCase())}`)
                            || entry.message
                            || 'Status updated';
                          const fallbackDescription = entry.description
                            || (entry.awb_number ? `AWB: ${entry.awb_number}` : undefined)
                            || (entry.title ? undefined : entry.message);
                          return (
                            <TimelineItem
                              key={`timeline-${entry.id || idx}`}
                              icon={
                                entry.type === 'payment_received' ? <CreditCard className="w-4 h-4" /> :
                                entry.type === 'note' ? <FileText className="w-4 h-4" /> :
                                <Clock className="w-4 h-4" />
                              }
                              title={fallbackTitle}
                              subtitle={fallbackDescription}
                              time={formatDate(entry.timestamp || entry.created_at)}
                              color={
                                entry.type === 'payment_received' ? 'text-green-400' :
                                entry.type === 'note' ? 'text-blue-400' :
                                entry.status === 'delivered' ? 'text-green-400' :
                                'text-gray-400'
                              }
                            />
                          );
                        });
                      })()}
                    </div>
                  </div>

                  {/* Shopify Fulfillments */}
                  {order.shopify_fulfillments && order.shopify_fulfillments.length > 0 && (
                    <div className="bg-wt-paper rounded-lg border border-wt-border p-4">
                      <h3 className="text-sm font-medium text-wt-text-muted mb-4">Shopify Fulfillments</h3>
                      <div className="space-y-3">
                        {order.shopify_fulfillments.map((f, idx) => (
                          <div key={idx} className="p-3 bg-wt-bg rounded-lg border border-wt-border">
                            <div className="flex items-center justify-between mb-2">
                              <Badge className={f.status === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                                {f.status}
                              </Badge>
                              <span className="text-xs text-wt-text-muted">{formatDate(f.created_at)}</span>
                            </div>
                            {f.tracking_number && (
                              <p className="text-sm text-wt-text-secondary">
                                Tracking: {f.tracking_number}
                                {f.tracking_url && (
                                  <a href={f.tracking_url} target="_blank" rel="noopener noreferrer" className="ml-2 text-blue-400">
                                    <ExternalLink className="w-3 h-3 inline" />
                                  </a>
                                )}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Shopify Transactions */}
                  {order.shopify_transactions && order.shopify_transactions.length > 0 && (
                    <div className="bg-wt-paper rounded-lg border border-wt-border p-4">
                      <h3 className="text-sm font-medium text-wt-text-muted mb-4">Payment Transactions</h3>
                      <div className="space-y-2">
                        {order.shopify_transactions.map((t, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-wt-bg rounded">
                            <div>
                              <span className={`text-sm font-medium ${t.status === 'success' ? 'text-green-400' : 'text-yellow-400'}`}>
                                {t.kind?.toUpperCase()}
                              </span>
                              <span className="text-xs text-wt-text-muted ml-2">via {t.gateway}</span>
                            </div>
                            <span className="text-sm text-wt-text-primary">{formatCurrency(parseFloat(t.amount))}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
      </AnimatePresence>, document.body)}

      {/* Split Shipment Dialog */}
      <Dialog open={splitDialogOpen} onOpenChange={setSplitDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <GitBranch className="h-5 w-5 text-indigo-400" />
              Split Order into Shipments
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 pt-4">
            {/* Original Order Info */}
            <div className="bg-wt-bg rounded-lg p-4 border border-wt-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-wt-text-primary">
                    Order: {order?.order_number}
                  </p>
                  <p className="text-xs text-wt-text-muted">
                    {order?.line_items?.length || 0} items · Total: {formatCurrency(order?.total_amount || order?.total_price || 0)}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={addShipment}
                  className="border-wt-border text-wt-text-secondary"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Shipment
                </Button>
              </div>
            </div>

            {/* Shipments */}
            <div className="grid gap-4">
              {splitShipments.map((shipment, shipmentIdx) => (
                <div
                  key={shipment.id}
                  className="bg-wt-surface border border-wt-border rounded-lg p-4"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center text-indigo-400 font-bold text-sm">
                        {shipmentIdx + 1}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-wt-text-primary">
                          Shipment {shipmentIdx + 1} of {splitShipments.length}
                        </p>
                        <p className="text-xs text-wt-text-muted">
                          {order?.order_number}-{shipmentIdx + 1} · {shipment.items.filter(i => i.quantity > 0).reduce((n, i) => n + i.quantity, 0)} unit(s)
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <p className="text-sm font-mono text-wt-gold" data-testid={`split-ship-total-${shipmentIdx + 1}`}>
                        {formatCurrency(calculateShipmentTotal(shipment))}
                      </p>
                      {splitShipments.length > 2 && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeShipment(shipment.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Items in this shipment */}
                  {shipment.items.length === 0 ? (
                    <div className="text-center py-8 border-2 border-dashed border-wt-border rounded-lg bg-wt-bg/40">
                      <Package className="h-8 w-8 mx-auto mb-2 text-wt-text-muted opacity-50" />
                      <p className="text-sm text-wt-text-muted">No items yet</p>
                      <p className="text-xs text-wt-text-muted mt-0.5">Use the → buttons on items above to move them here</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {shipment.items.filter(item => item.quantity > 0).map((item) => (
                        <div
                          key={item.line_item_id}
                          className="flex items-center gap-3 p-3 bg-wt-bg rounded-lg border border-wt-border/40"
                          data-testid={`split-item-${item.line_item_id}-s${shipmentIdx + 1}`}
                        >
                          {item.image && (
                            <img
                              src={item.image}
                              alt={item.title}
                              className="w-12 h-12 rounded object-cover"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-wt-text-primary truncate" title={item.title}>
                              {item.title}
                            </p>
                            <div className="flex items-center gap-2 mt-0.5">
                              {item.variant_title && (
                                <span className="text-xs text-indigo-300">{item.variant_title}</span>
                              )}
                              {item.sku && (
                                <span className="text-xs text-wt-text-muted">SKU: {item.sku}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 bg-wt-surface rounded-md px-1" title="Qty in this shipment">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => updateShipmentItemQuantity(shipment.id, item.line_item_id, item.quantity - 1)}
                              disabled={item.quantity <= 0}
                              className="h-7 w-7 p-0"
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="w-6 text-center text-sm font-semibold text-wt-text-primary">
                              {item.quantity}
                            </span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => updateShipmentItemQuantity(shipment.id, item.line_item_id, item.quantity + 1)}
                              disabled={item.quantity >= item.max_quantity}
                              className="h-7 w-7 p-0"
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                          <p className="text-sm font-mono text-wt-text-secondary w-24 text-right">
                            {formatCurrency(item.price * item.quantity)}
                          </p>
                          {/* Move to other shipment buttons */}
                          <div className="flex items-center gap-1">
                            {splitShipments.filter(s => s.id !== shipment.id).map((targetShipment) => {
                              const targetIdx = splitShipments.findIndex(s => s.id === targetShipment.id) + 1;
                              return (
                                <Button
                                  key={targetShipment.id}
                                  size="sm"
                                  variant="outline"
                                  onClick={() => moveItemToShipment(shipment.id, targetShipment.id, item.line_item_id, 1)}
                                  className="h-7 px-2 text-xs border-wt-border text-wt-text-secondary hover:text-indigo-300 hover:border-indigo-400/50"
                                  title={`Move 1 unit to Shipment ${targetIdx}`}
                                >
                                  <ArrowRight className="h-3 w-3 mr-0.5" />
                                  Ship {targetIdx}
                                </Button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Summary */}
            <div className="bg-wt-bg rounded-lg p-4 border border-wt-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-wt-text-muted">
                    Splitting into {splitShipments.filter(s => s.items.length > 0).length} shipments
                  </p>
                  <p className="text-xs text-wt-text-muted mt-1">
                    Each shipment will have its own order number and can be assigned to different drivers/couriers
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-wt-text-muted">Total Amount</p>
                  <p className="text-lg font-bold text-wt-gold">
                    {formatCurrency(splitShipments.reduce((sum, s) => sum + calculateShipmentTotal(s), 0))}
                  </p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-wt-border">
              <Button
                variant="ghost"
                onClick={() => setSplitDialogOpen(false)}
                className="text-wt-text-secondary"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSplitOrder}
                disabled={splitting || splitShipments.filter(s => s.items.length > 0 && s.items.some(i => i.quantity > 0)).length < 2}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                {splitting ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <GitBranch className="h-4 w-4 mr-2" />
                )}
                Split Order
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Issue Store Credit Dialog — manual recovery when the
          automated journey didn't post the credit. */}
      <Dialog open={issueCreditOpen} onOpenChange={setIssueCreditOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-md" data-testid="issue-credit-dialog">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-emerald-400" />
              Issue Store Credit
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="text-xs text-wt-text-muted">
              Order <span className="font-mono text-wt-gold">#{order.order_number}</span> · Customer{' '}
              <span className="text-wt-text-primary">{order.customer_name || order.customer_email || order.customer_phone || '—'}</span>
            </div>

            <div>
              <Label className="text-xs text-wt-text-secondary">Amount type</Label>
              <div className="mt-1 flex gap-2">
                {[
                  { v: 'fixed', label: 'Fixed amount' },
                  { v: 'percentage', label: '% of order total' },
                ].map(opt => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setCreditForm(f => ({ ...f, amount_type: opt.v }))}
                    className={`flex-1 px-3 py-1.5 rounded text-xs border transition ${
                      creditForm.amount_type === opt.v
                        ? 'bg-emerald-500/10 border-emerald-400 text-emerald-300'
                        : 'bg-wt-bg border-wt-border text-wt-text-muted hover:text-wt-text-primary'
                    }`}
                    data-testid={`credit-type-${opt.v}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {creditForm.amount_type === 'fixed' ? (
              <div>
                <Label className="text-xs text-wt-text-secondary">Credit amount ({order.currency || 'local'})</Label>
                <Input
                  type="number" min="0" step="0.01"
                  value={creditForm.amount}
                  onChange={(e) => setCreditForm(f => ({ ...f, amount: e.target.value }))}
                  placeholder="e.g. 87.45"
                  data-testid="credit-amount-input"
                  className="bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>
            ) : (
              <div>
                <Label className="text-xs text-wt-text-secondary">Percentage of order total (%)</Label>
                <Input
                  type="number" min="0" max="100" step="0.5"
                  value={creditForm.percentage}
                  onChange={(e) => setCreditForm(f => ({ ...f, percentage: e.target.value }))}
                  placeholder="e.g. 5"
                  data-testid="credit-percentage-input"
                  className="bg-wt-bg border-wt-border text-wt-text-primary"
                />
                <p className="text-[10px] text-wt-text-muted mt-1">
                  Projected credit: {creditForm.percentage && order.total_amount
                    ? (parseFloat(creditForm.percentage) * parseFloat(order.total_amount) / 100).toFixed(2)
                    : '0.00'} {order.currency || ''}
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-wt-text-secondary">Expires in (days)</Label>
                <Input
                  type="number" min="0" max="3650"
                  value={creditForm.expires_in_days}
                  onChange={(e) => setCreditForm(f => ({ ...f, expires_in_days: e.target.value }))}
                  data-testid="credit-expires-input"
                  className="bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 text-xs text-wt-text-secondary cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={creditForm.push_to_shopify}
                    onChange={(e) => setCreditForm(f => ({ ...f, push_to_shopify: e.target.checked }))}
                    data-testid="credit-push-shopify-toggle"
                  />
                  Push to Shopify
                </label>
              </div>
            </div>

            <div>
              <Label className="text-xs text-wt-text-secondary">Reason / note (visible to customer)</Label>
              <Input
                value={creditForm.reason}
                onChange={(e) => setCreditForm(f => ({ ...f, reason: e.target.value }))}
                placeholder="Order Delivered cashback"
                data-testid="credit-reason-input"
                className="bg-wt-bg border-wt-border text-wt-text-primary"
              />
            </div>

            {creditResult && (
              <div className="p-3 rounded-md bg-wt-bg border border-wt-border text-xs space-y-1" data-testid="credit-result">
                <div className="text-emerald-300">✓ Local credit posted: {creditResult.amount} ({creditResult.balance_before} → {creditResult.balance_after})</div>
                <div className={creditResult.shopify_push?.success ? 'text-emerald-300' : 'text-rose-300'}>
                  {creditResult.shopify_push?.success
                    ? `✓ Shopify push succeeded (tx: ${creditResult.shopify_push.transaction_id || '—'})`
                    : creditResult.shopify_push?.attempted
                      ? `✗ Shopify push failed: ${creditResult.shopify_push.error}`
                      : '— Shopify push not attempted'}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setIssueCreditOpen(false)} className="text-wt-text-secondary">
              Close
            </Button>
            <Button
              onClick={handleIssueCredit}
              disabled={issuingCredit}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="confirm-issue-credit"
            >
              {issuingCredit ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Issuing…</> : 'Issue Credit'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <EditOrderDialog
        open={editOrderOpen}
        onClose={() => setEditOrderOpen(false)}
        order={order}
        formatCurrency={formatCurrency}
        onSaved={(updatedOrder) => {
          if (onRefresh && updatedOrder) onRefresh(updatedOrder);
        }}
      />

      <Dialog open={cancelOrderOpen} onOpenChange={(o) => { if (!o) setCancelOrderOpen(false); }}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-md" data-testid="cancel-order-dialog">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-400" />
              Cancel Order {order?.order_number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-wt-text-secondary">
              This marks the order as cancelled in Opswarex. It is not cancelled on the sales channel — do that separately if needed.
            </p>
            <div>
              <Label className="text-xs text-wt-text-secondary">Reason (optional)</Label>
              <Input
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="e.g. Customer requested cancellation"
                className="bg-wt-subtle border-wt-border text-wt-text-primary mt-1"
                data-testid="cancel-order-reason-input"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCancelOrderOpen(false)}
                className="border-wt-border text-wt-text-primary" data-testid="cancel-order-back-btn">
                Keep Order
              </Button>
              <Button onClick={handleCancelOrder} disabled={cancellingOrder}
                className="bg-red-600 hover:bg-red-700 text-white" data-testid="cancel-order-confirm-btn">
                {cancellingOrder ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <XCircle className="w-4 h-4 mr-1" />}
                {cancellingOrder ? 'Cancelling...' : 'Cancel Order'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={deliveryDateOpen} onOpenChange={(o) => { if (!o) setDeliveryDateOpen(false); }}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-sm" data-testid="set-delivery-date-dialog">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-wt-gold" />
              Set Delivery Date — {order?.order_number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-wt-text-secondary">
              Pins the promised delivery date the customer asked for. It replaces the computed estimate everywhere.
            </p>
            <div>
              <Label className="text-xs text-wt-text-secondary">Promised date</Label>
              <DateInput
                value={deliveryDateInput}
                min={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setDeliveryDateInput(e.target.value)}
                className="w-full mt-1 px-2 py-2 rounded bg-wt-subtle border border-wt-border text-sm text-wt-text-primary"
                data-testid="set-delivery-date-input"
              />
              {order?.rescheduled_delivery_date && (
                <p className="text-xs text-wt-gold mt-1" data-testid="current-promised-date">Current: {order.rescheduled_delivery_date}</p>
              )}
            </div>
            <div className="flex justify-end gap-2">
              {order?.rescheduled_delivery_date && (
                <Button variant="outline" onClick={() => saveDeliveryDate(null)} disabled={savingDeliveryDate}
                  className="border-red-500/40 text-red-400 hover:bg-red-500/10" data-testid="clear-delivery-date-btn">
                  Clear Date
                </Button>
              )}
              <Button onClick={() => { if (!deliveryDateInput) { toast.error('Pick a date first'); return; } saveDeliveryDate(deliveryDateInput); }}
                disabled={savingDeliveryDate}
                className="bg-wt-gold text-black hover:bg-wt-gold/90" data-testid="save-delivery-date-btn">
                {savingDeliveryDate ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                Save Date
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Timeline Item Component
const TimelineItem = ({ icon, title, subtitle, time, color }) => (
  <div className="flex gap-3">
    <div className={`flex-shrink-0 w-8 h-8 rounded-full bg-wt-bg border border-wt-border flex items-center justify-center ${color}`}>
      {icon}
    </div>
    <div className="flex-1 pb-4 border-l border-wt-border -ml-4 pl-6">
      <p className="text-sm font-medium text-wt-text-primary">{title}</p>
      {subtitle && <p className="text-xs text-wt-text-muted mt-0.5">{subtitle}</p>}
      <p className="text-xs text-wt-text-muted mt-1">{time}</p>
    </div>
  </div>
);

export default OrderDetailPanel;
